/*
 Generated by typeshare 1.13.3
*/

/** Stable identifier for an action exposed through automation surfaces. */
export type ActionId = string;

export type AttributeTransitions = Record<string, PartialTransition>;

/** A hardware DMX channel value (0-255). */
export type ChannelDmxValue = number;

/** Stable showfile identifier for a color path object. */
export type ColorPathId = number;

export type Uuid = string;

/** Identifies one externally accepted command from ingress through its terminal result. */
export type CommandId = Uuid;

/** Flow identifier. */
export type FlowId = number;

/** Node identifier, stable within a flow. */
export type FlowNodeId = number;

/** Port identifier, stable within a node. */
export type FlowPortId = number;

/** Port version, increments on port value changes. */
export type FlowPortVersion = number;

/** Event sequence id for Trigger ports. */
export type FlowSeq = number;

/** Flow version, increments on topology/config changes. */
export type FlowVersion = number;

/**
 * Universal identifier for any running instance.
 * 
 * This is a runtime identifier, distinct from source IDs (Sequence/FX asset UUIDs).
 */
export type InstanceId = Uuid;

/** Stable identity for one runtime use of a registered action. */
export type InvocationId = string;

export type Ipv4Addr = string;

/** Identifies one internal engine action or cross-domain request execution. */
export type OperationId = Uuid;

/** A logical DMX channel value for a parameter, that may be clamped or split into multiple hardware channels on output. */
export type ParameterDmxValue = number;

export type Percentage = number;

/** Priority indicator, where lower priority values are first and higher priorities are last. */
export type Priority = number;

/** Stable identity for one independently controlled Step FX editor preview. */
export type StepFxPreviewSessionId = string;

/** Identifies the undo group that owns mutations produced by related work. */
export type UndoId = Uuid;

/** TypeScript-facing shape for Rust `Duration` values. */
export interface Duration {
	secs: number;
	nanos: number;
}

/** The action kind to invoke when a timeline action is triggered. */
export type ActionKind = 
	/** Trigger a cue from the cue library implicitly tied to an transient clip that removes itself when done. */
	| { type: "FireCue", data: Uuid }
	/** Start a clip */
	| { type: "StartClip", data: Uuid }
	/** Stop a clip */
	| { type: "StopClip", data: Uuid }
	/** Advance a sequence on a clip */
	| { type: "AdvanceSequence", data: Uuid }
	/** Go back in a sequence on a clip */
	| { type: "BackSequence", data: Uuid }
	/** Set the rate multiplier for a clip's active playback. */
	| { type: "SetClipRate", data: {
	/** The unique ID of the clip to control. */
	uid: Uuid;
	/** Playback clock rate multiplier. */
	rate: number;
}}
	/** Jump to a specific cue in a sequence on a clip */
	| { type: "JumpToCue", data: {
	/** The unique ID of the clip to jump to */
	uid: Uuid;
	/** The cue index to jump to (starts counting from 1) */
	cue_index: number;
}}
	/** Dispatch a desk command string through the shared eval path. */
	| { type: "DeskEval", data: string }
	/** Dispatch a registered action reference. */
	| { type: "RegisteredAction", data: ActionReference };

/** An action positioned on a track that triggers an action when timecode reaches its position */
export interface Action {
	/** Unique identifier for this action */
	id: string;
	/** Display name of the action */
	label: string;
	/** Position in milliseconds from the start of the timeline */
	position: Duration;
	/** Duration in milliseconds */
	duration: Duration;
	/** The action to perform when this action is triggered */
	action: ActionKind;
}

/** Surface that can invoke a registered action. */
export enum ActionSurface {
	/** A timeline action invoked the action. */
	Timeline = "timeline",
	/** A MIDI mapping invoked the action. */
	Midi = "midi",
	/** An OSC mapping invoked the action. */
	Osc = "osc",
	/** A command palette entry invoked the action. */
	CommandPalette = "commandPalette",
	/** A websocket command invoked the action. */
	Websocket = "websocket",
}

/** Metadata describing one action available to automation surfaces. */
export interface ActionDescriptor {
	/** Stable action identifier. */
	id: ActionId;
	/** User-facing action label. */
	label: string;
	/** Surfaces allowed to store or invoke the action. */
	allowed_surfaces: ActionSurface[];
	/** JSON Schema describing the action's persisted arguments. */
	argument_schema: unknown;
}

/** Stored reference to a registered action plus domain-owned arguments. */
export interface ActionReference {
	/** Stable action identifier to dispatch. */
	id: ActionId;
	/** Arguments interpreted only by the domain that registered the action. */
	arguments: unknown;
}

/** Serialized Dockview panel metadata stored alongside a named panel layout. */
export interface StoredPanelLayoutPanel {
	/** Stable panel id inside the serialized Dockview layout. */
	id: string;
	/** User-visible panel title captured when the layout was stored. */
	title: string;
	/** Panel parameters needed by Dockview to restore component state. */
	params: unknown;
}

/** Serialized Dockview panel layout saved as the showfile's active UI layout. */
export interface ActivePanelLayout {
	/** Named layout owning this working arrangement. */
	layoutId?: string;
	/** Layout storage format version. */
	version: number;
	/** Opaque Dockview layout payload. */
	layout: unknown;
	/** Panel metadata captured when the layout was stored. */
	panels: StoredPanelLayoutPanel[];
	/** Last layout update timestamp in milliseconds since Unix epoch. */
	updatedAt: number;
}

/**
 * Attributes are the properties of a fixture that can be controlled
 * DMX attribute names understood by fixture definitions and command parsing.
 */
export type Attribute = 
	| { type: "Intensity", data?: undefined }
	| { type: "VirtualIntensity", data?: undefined }
	| { type: "Pan", data?: undefined }
	| { type: "Tilt", data?: undefined }
	| { type: "StrobeShutter", data?: undefined }
	| { type: "StrobeRate", data?: undefined }
	| { type: "Red", data?: undefined }
	| { type: "Green", data?: undefined }
	| { type: "Blue", data?: undefined }
	| { type: "Amber", data?: undefined }
	| { type: "White", data?: undefined }
	| { type: "WarmWhite", data?: undefined }
	| { type: "CoolWhite", data?: undefined }
	| { type: "Cyan", data?: undefined }
	| { type: "Magenta", data?: undefined }
	| { type: "Yellow", data?: undefined }
	| { type: "UV", data?: undefined }
	| { type: "Raw", data?: undefined }
	| { type: "Gobo", data?: undefined }
	| { type: "GoboRot", data?: undefined }
	| { type: "Shaper", data?: undefined }
	| { type: "Prism", data?: undefined }
	| { type: "Frost", data?: undefined }
	| { type: "Zoom", data?: undefined }
	| { type: "Custom", data: {
	label: string;
}};

/** Attribute categories are used to group attributes together */
export enum AttributeCategory {
	Dimmer = "Dimmer",
	Position = "Position",
	Gobo = "Gobo",
	Color = "Color",
	Beam = "Beam",
	Focus = "Focus",
	Control = "Control",
	Other = "Other",
}

/** Presentation metadata for a fixture attribute. */
export interface AttributeMetadata {
	/** Canonical stable key used by clients for lookup. */
	key: string;
	/** Attribute value represented by this metadata row. */
	attribute: Attribute;
	/** Human-readable display label. */
	label: string;
	/** Canonical attribute category. */
	category: AttributeCategory;
	/** Canonical presentation sort order. */
	sort_order: number;
}

/** Properties specific to audience areas */
export interface AudienceProperties {
	/** Width of the audience area in meters (stage left-right) */
	width: number;
	/** Depth of the audience area in meters (upstage-downstage) */
	depth: number;
	/** Density of audience members per square meter */
	density: number;
	/** Height variation of audience members (randomness factor 0-1) */
	heightVariation: number;
}

/** A point on a parameter curve */
export interface AutomationPoint {
	/** Time position in milliseconds */
	position: Duration;
	/** Parameter value (normalized 0-1) */
	value: number;
}

/** The type of parameter and what it controls */
export type ParameterType = 
	/** Controls a global variable */
	| { type: "GlobalVariable", data: string }
	/** Controls the rate master (speed) of a clip */
	| { type: "RateMaster", data: Uuid };

/** A parameter that can be adjusted over time with a curve of control points */
export interface AutomationLane {
	/** Unique identifier for this automation lane */
	id: string;
	/** Display name of the parameter */
	name: string;
	/** CSS color for display in the UI */
	color: string;
	/** Control points that define the parameter curve */
	points: AutomationPoint[];
	/** The type of parameter and what it controls */
	parameter_type: ParameterType;
}

/** Information about an available fixture */
export interface AvailableFixtureInfo {
	/** Manufacturer name */
	make: string;
	/** Model name */
	model: string;
	/** Available modes */
	modes: string[];
	/** Source format (GDTF, OFL, or built-in) */
	source_format: string;
	/** Deterministic content fingerprint of the fixture source file */
	asset_etag: string;
}

/** Available WebAssembly fx module discovered in the app data module directory. */
export interface AvailableFxModuleInfo {
	/** Module name used by stored fx module definitions, without the `.wasm` suffix. */
	name: string;
	/** File name found in the app data `fx-modules` directory. */
	filename: string;
}

/** Information about an available object */
export interface AvailableObjectInfo {
	/** Object name */
	name: string;
	/** Object category */
	category: string;
	/** Object description */
	description?: string;
	/** Default scale */
	scale: number;
	/** Tags for searching */
	tags?: string[];
	/** Encoded object bundle token for `/api/object-model/{object_path}` */
	modelPath: string;
	/** Deterministic content fingerprint of the object bundle */
	assetVersion: string;
}

/** Beam rendering style, determines which shader is used for visualization. */
export enum BeamType {
	/** Hard-edged beam with sharp cutoff (e.g., profile spots, followspots) */
	Spot = "spot",
	/** Soft-edged beam with gradual falloff (e.g., wash lights, PARs) */
	Wash = "wash",
	/** Soft-edged beam with Fresnel lens characteristics */
	Fresnel = "fresnel",
	/** Soft-edged beam with plano-convex lens characteristics */
	Pc = "pc",
	/**
	 * Glow/pixel fixtures that emit light but shouldn't render volumetric beams
	 * (e.g., LED bars, pixel fixtures, GDTF Glow/Rectangle/None beam types)
	 */
	Glow = "glow",
}

/** Beam properties for visualization, derived from FixturePhysical. */
export interface BeamSpec {
	/** Inner cone angle in degrees (GDTF BeamAngle) */
	beamAngle: number;
	/** Outer cone angle in degrees (GDTF FieldAngle) */
	fieldAngle: number;
	/** Light output in lumens */
	lumens: number;
	/** Beam rendering style */
	beamType: BeamType;
}

/** A detected beat marker on the timeline. */
export interface BeatMarker {
	/** Beat position in timeline time. */
	time: Duration;
	/** Zero-based beat index. */
	beat_index: number;
	/** Whether this marker is considered a downbeat (bar start). */
	is_downbeat: boolean;
	/** Optional per-marker confidence score in 0..1. */
	confidence?: number;
}

/** Source of beatgrid detection data */
export enum BeatgridSource {
	/** Beatgrid generated automatically after audio changes */
	Auto = "auto",
	/** Beatgrid generated from explicit user request */
	Manual = "manual",
}

/** Persisted beatgrid analysis data for a timeline. */
export interface BeatgridData {
	/** Origin of the currently stored beatgrid. */
	source: BeatgridSource;
	/** Fingerprint of the audio content used to generate this beatgrid. */
	audio_fingerprint: string;
	/** Estimated tempo in beats per minute. */
	bpm: number;
	/** Beats per bar used for downbeat inference. */
	beats_per_bar: number;
	/** Beat and downbeat markers. */
	markers: BeatMarker[];
	/** Overall confidence score in 0..1. */
	confidence: number;
}

/** Notification emitted when beatgrid detection fails. */
export interface BeatgridDetectionFailed {
	/** Timeline UID associated with the request. */
	timeline_uid: string;
	/** Request identifier. */
	request_id: string;
	/** Error details. */
	error: string;
}

/** Transient beatgrid proposal returned by detector before apply/reject. */
export interface BeatgridProposal {
	/** Request identifier for apply/reject. */
	request_id: string;
	/** Origin of the proposal. */
	source: BeatgridSource;
	/** Fingerprint of the analyzed audio. */
	audio_fingerprint: string;
	/** Estimated tempo in beats per minute. */
	bpm: number;
	/** Beats per bar used for downbeat inference. */
	beats_per_bar: number;
	/** Proposed beat and downbeat markers. */
	markers: BeatMarker[];
	/** Overall confidence score in 0..1. */
	confidence: number;
}

/** Notification emitted when beatgrid detection produced a proposal. */
export interface BeatgridDetectionReady {
	/** Timeline UID associated with the request. */
	timeline_uid: string;
	/** Proposed beatgrid data. */
	proposal: BeatgridProposal;
}

/** Notification emitted when a beatgrid detection request starts. */
export interface BeatgridDetectionStarted {
	/** Timeline UID associated with the request. */
	timeline_uid: string;
	/** Request identifier. */
	request_id: string;
}

/** Normalized two-dimensional point used by Bézier curves. */
export interface Point2D {
	/** Horizontal coordinate. */
	x: number;
	/** Vertical coordinate. */
	y: number;
}

/** Cubic Bézier transition curve. */
export interface Bezier {
	/** First control point. */
	cp1: Point2D;
	/** Second control point. */
	cp2: Point2D;
}

/** Validation mode for binding overlaps. */
export enum BindingValidationMode {
	/** Enforce all overlap rules. */
	Strict = "Strict",
	/** Allow overlaps except for console address uniqueness. */
	Permissive = "Permissive",
}

/** Resource settings for binding validation. */
export interface BindingValidationSettings {
	/** Validation mode. */
	mode: BindingValidationMode;
}

/** Input binding source. */
export type InputSource = 
	/** Transport input (sACN/Art-Net/uDMX). */
	| { type: "Transport", data: {
	/** Transport identifier. */
	transport: BindingTransport;
	/** Optional universe range. */
	universe?: DmxRange;
	/** Optional address. */
	address?: number;
}}
	/** Console input. */
	| { type: "Console", data: {
	/** Optional universe range. */
	universe?: DmxRange;
	/** Optional address. */
	address?: number;
}}
	/** Fixture input. */
	| { type: "Fixture", data: {
	/** Fixture UIDs. */
	uids: Uuid[];
	/** Optional element index. */
	element?: number;
	/** Optional parameter name. */
	param?: string;
}};

/** Input binding target. */
export type InputTarget = 
	/** Transport target. */
	| { type: "Transport", data: {
	/** Output transport target identifier. */
	target: string;
	/** Optional universe range. */
	universe?: DmxRange;
	/** Optional address. */
	address?: number;
}}
	/** Console target. */
	| { type: "Console", data: {
	/** Optional universe range. */
	universe?: DmxRange;
	/** Optional address. */
	address?: number;
}}
	/** Fixture target. */
	| { type: "Fixture", data: {
	/** Fixture UIDs. */
	uids: Uuid[];
	/** Optional element index. */
	element?: number;
	/** Optional parameter name. */
	param?: string;
}}
	/** Disabled target (filter). */
	| { type: "Disabled", data?: undefined };

/** Binding declarations for input rules. */
export interface InputBinding {
	/** Binding source. */
	source: InputSource;
	/** Binding target. */
	target: InputTarget;
	/** Priority (lower runs first). */
	priority: number;
	/** If true, duplicate the source address across a range destination. */
	clone: boolean;
}

/** Output binding source. */
export type OutputSource = 
	/** Fixture source. */
	| { type: "Fixture", data: {
	/** Fixture UIDs. */
	uids: Uuid[];
	/** Optional element index. */
	element?: number;
	/** Optional parameter name. */
	param?: string;
}}
	/** Console source. */
	| { type: "Console", data: {
	/** Optional universe range. */
	universe?: DmxRange;
	/** Optional address. */
	address?: number;
}};

/** Output binding target. */
export type OutputTarget = 
	/** Transport target. */
	| { type: "Transport", data: {
	/** Output transport target identifier. */
	target: string;
	/** Optional universe range. */
	universe?: DmxRange;
	/** Optional address. */
	address?: number;
}}
	/** Console target. */
	| { type: "Console", data: {
	/** Optional universe range. */
	universe?: DmxRange;
	/** Optional address. */
	address?: number;
}}
	/** Disabled target (filter). */
	| { type: "Disabled", data?: undefined };

/** Binding declarations for output rules. */
export interface OutputBinding {
	/** Binding source. */
	source: OutputSource;
	/** Binding target. */
	target: OutputTarget;
	/** Priority (lower runs first). */
	priority: number;
	/** If true, duplicate the source address across a range destination. */
	clone: boolean;
}

/** Disabled binding filter rule. */
export type DisabledBinding = 
	/** Disable input bindings that match the source. */
	| { type: "Input", data: {
	/** Binding source to disable. */
	source: InputSource;
	/** Priority (lower runs first). */
	priority: number;
	/** If true, duplicate the source address across a range destination. */
	clone: boolean;
}}
	/** Disable output bindings that match the source. */
	| { type: "Output", data: {
	/** Binding source to disable. */
	source: OutputSource;
	/** Priority (lower runs first). */
	priority: number;
	/** If true, duplicate the source address across a range destination. */
	clone: boolean;
}};

/** Binding snapshot for UI consumption. */
export interface BindingsSnapshot {
	/** Input bindings. */
	input: InputBinding[];
	/** Output bindings. */
	output: OutputBinding[];
	/** Disabled bindings. */
	disabled: DisabledBinding[];
}

/** Stable identity fields shared by persisted showfile objects. */
export interface Identifiers {
	id: number;
	uid: string;
	label: string;
}

/** Controls whether a store keeps references to source objects or captures copies. */
export enum InclusionMode {
	REFERENCE = "REFERENCE",
	COPY = "COPY",
}

/** Predicate used to include or exclude fixtures from a store. */
export type FilterType = 
	| { type: "FixtureType", data: {
	make: string;
	model: string;
}}
	| { type: "AttributeCategory", data: {
	category: AttributeCategory;
}}
	| { type: "Attribute", data: {
	attribute: Attribute;
}};

/** Inclusion and exclusion rules that decide which fixtures belong to a store. */
export interface InclusionSettings {
	inclusion_mode: InclusionMode;
	inclusion_filters: FilterType[];
	exclusion_filters: FilterType[];
}

/** Cross-object references stored by an object that depends on palettes or effects. */
export interface References {
	palettes: Uuid[];
	fx: Uuid[];
}

/** Blueprint - selection-less attribute value storage */
export interface Blueprint {
	/** Identifiers for the blueprint */
	identifiers: Identifiers;
	/** Logical values captured without fixture or selection identity. */
	values: Record<string, ValueSource>;
	/** Settings for including/excluding attributes */
	inclusion_settings: InclusionSettings;
	/** References to other objects */
	references: References;
}

/** Logical portion of a Blueprint retained by a referenced application. */
export type BlueprintSelector = 
	/** Apply every logical attribute currently contained by the Blueprint. */
	| { type: "All", data?: undefined }
	/** Apply the single requested logical attribute. */
	| { type: "Attribute", data: Attribute }
	/** Apply every current Blueprint attribute in the requested category. */
	| { type: "Category", data: AttributeCategory };

/** Stable live relationship between a cue instruction and a Blueprint definition. */
export interface BlueprintApplication {
	/** Stable identity of the referenced Blueprint. */
	blueprint_uid: string;
	/** Logical attributes selected dynamically from the current definition. */
	selector: BlueprintSelector;
}

/** AST representation of a selection (fixtures or groups that resolve to fixtures) */
export type SelectionExpr = 
	/** Reference to fixtures by ID expression */
	| { type: "Fixture", data: UnresolvedFixtureRef }
	/** Reference to fixtures by ID range */
	| { type: "FixtureRange", data: {
	/** Start fixture reference */
	start: UnresolvedFixtureRef;
	/** End fixture reference */
	end: UnresolvedFixtureRef;
}}
	/** Map fixture range to element selector (cartesian expansion). */
	| { type: "FixtureMap", data: {
	/** Fixture ID range (inclusive). */
	fixtures: FixtureRangeExpr;
	/** Element selector applied to each fixture in the range. */
	elements: ElementSelectorExpr;
}}
	/** Reference to groups by explicit identity expression (resolves to fixtures) */
	| { type: "Group", data: GroupRefExpr }
	/** Addition of two selections */
	| { type: "Add", data: {
	/** Left-hand side of the addition */
	lhs: SelectionExpr;
	/** Right-hand side of the addition */
	rhs: SelectionExpr;
}}
	/** Subtraction of two selections */
	| { type: "Sub", data: {
	/** Left-hand side of the subtraction */
	lhs: SelectionExpr;
	/** Right-hand side of the subtraction */
	rhs: SelectionExpr;
}}
	/** An explicit selection set that preserves span boundaries. */
	| { type: "Span", data: SelectionExpr }
	/** A parenthesized spatial selection whose resolved indexes compose as source spans. */
	| { type: "Spatial", data: SpatialSelection }
	/** A resolved list of fixtures (for cases where we already have the resolved fixtures) */
	| { type: "Resolved", data: FixtureRef[] };

/** Transform applied to a source selection during spatial selection resolution. */
export type SpatialClause = 
	| { type: "Grid", data: GridSize }
	| { type: "Transpose", data?: undefined }
	| { type: "Mirror", data: Axis }
	| { type: "RotateLeft", data?: undefined }
	| { type: "RotateRight", data?: undefined }
	| { type: "Split", data?: undefined }
	| { type: "Merge", data?: undefined }
	| { type: "Expand", data: {
	depth?: number;
}}
	| { type: "Take", data: number }
	| { type: "Skip", data: number }
	| { type: "Shift", data: {
	axis: Axis;
	amount: number;
}}
	| { type: "Blocks", data: {
	axis: Axis;
	amount: number;
}}
	| { type: "Group", data: {
	axis: Axis;
	amount: number;
}}
	| { type: "Wings", data: {
	axis: Axis;
	amount: number;
}}
	| { type: "Shuffle", data: {
	axis: Axis;
	seed: number;
}}
	| { type: "Invert", data: {
	mode: InvertMode;
	attrs?: Attribute[];
}};

/** Source selection plus the ordered spatial transforms to apply to it. */
export interface SpatialSelection {
	/** Source expression resolved before replaying this pipeline's clauses. */
	source: SelectionExpr;
	/** Ordered transforms applied to the resolved source. */
	clauses: SpatialClause[];
	/** Additional spatial pipelines concatenated after this pipeline. */
	union?: SpatialSelection[];
}

/** Reference to either a whole fixture or a specific indexed element within it. */
export interface FixtureRef {
	fixture_uid: Uuid;
	index?: number;
}

/** Per-fixture transition overrides grouped by attribute. */
export interface FixtureAttributeTransition {
	/** Fixture or fixture element this override applies to. */
	fixture: FixtureRef;
	/** Attribute-specific transition overrides for this fixture ref. */
	transitions_by_attribute: Record<string, PartialTransition>;
}

/** Timing distribution used to resolve a delay or fade duration for each target. */
export type TransitionMode = 
	| { type: "Fixed", data: Duration }
	| { type: "Interpolated", data: {
	start: Duration;
	end: Duration;
}}
	| { type: "Manual", data: Duration[] };

/** Easing curve used to map transition progress to output intensity. */
export enum FadeCurve {
	Linear = "Linear",
	EaseIn = "EaseIn",
	EaseOut = "EaseOut",
	EaseInOut = "EaseInOut",
}

/** TypeScript-facing shape for partially specified transition updates. */
export interface PartialTransition {
	delay_in?: TransitionMode;
	fade_in?: TransitionMode;
	curve_in?: FadeCurve;
	delay_out?: TransitionMode;
	fade_out?: TransitionMode;
	curve_out?: FadeCurve;
}

/**
 * Stores an instruction without a selection
 * 
 * Used by the programmer (active selection events) and palettes
 */
export interface CueInstruction {
	/** The values for each attribute */
	values: Record<string, ValueSource>;
	/** Optional live Blueprint operation occupying this instruction's authoring position. */
	blueprint_application?: BlueprintApplication;
	/**
	 * Per-transition attribute that override the instruction-wide transitions
	 * #[serde_as(as = "Vec<(_, _)>")]
	 */
	transitions_by_attribute: Record<string, PartialTransition>;
	/**
	 * Per-fixture, per-attribute transitions that override instruction-wide and
	 * attribute-wide transitions.
	 */
	transitions_by_fixture_attribute?: FixtureAttributeTransition[];
	/** Optional color path applied to logical color transitions in this instruction. */
	color_path_id?: ColorPathId;
	/** Default transition that applies to all attributes */
	transitions: PartialTransition;
}

/** Wrapper struct to bind a selection to the instructions */
export interface BoundCueInstruction {
	/** The spatial selection for the instruction */
	selection: SpatialSelection;
	/** The cue instruction to execute */
	cue_instruction: CueInstruction;
}

/** Sources that can be assigned to clips. */
export type Source = 
	/** Cue sequence identified by persistent UUID. */
	| { type: "Sequence", data: Uuid }
	/** Parameter effect identified by persistent UUID. */
	| { type: "Fx", data: Uuid }
	/** Flow graph identified by persistent UUID. */
	| { type: "Flow", data: Uuid }
	/** Step effect identified by persistent UUID. */
	| { type: "StepFx", data: Uuid }
	/** Effect module identified by persistent UUID. */
	| { type: "FxModule", data: Uuid };

/** Persistent playback behavior stored with a clip. */
export interface ClipOptions {
	/** Whether the clip should release current playback output after stopping. */
	auto_release: boolean;
	/** Whether a non-wrapping sequence clip should stop after the final cue completes. */
	deactivate_on_sequence_end: boolean;
}

/** Persistent clip configuration stored in a showfile. */
export interface Clip {
	/** Stable numeric, UUID, and label identifiers. */
	identifiers: Identifiers;
	/** Source to run when the clip starts. */
	source?: Source;
	/** Layer priority for spawned instances. */
	priority: Priority;
	/** Clip-local playback behavior. */
	options?: ClipOptions;
}

/** Persisted clip target interpreted by desk-owned action registrations. */
export type ClipTarget = 
	/** User-facing numeric clip identifier. */
	| { type: "Id", data: number }
	/** Persistent clip UID. */
	| { type: "Uid", data: Uuid };

/** Persisted arguments shared by clip lifecycle actions. */
export interface ClipActionArguments {
	/** Clip addressed by the action. */
	target: ClipTarget;
}

/** Optional per-attribute timing override. */
export interface ColorAttributeTiming {
	/** Delay as a fraction of the parent cue fade. */
	delay_percent: number;
	/** Active fade duration as a fraction of the parent cue fade. */
	time_percent: number;
	/** Curve used while this attribute moves. */
	curve: FadeCurve;
}

/** Interpolation space used by a color path. */
export enum ColorInterpolationSpace {
	/** Red, green, and blue additive components. */
	Rgb = "Rgb",
	/** Hue, saturation, and value. */
	Hsv = "Hsv",
	/** Cyan, magenta, and yellow subtractive components. */
	Cmy = "Cmy",
	/** Placeholder for future CIE xyY support. */
	XyY = "XyY",
	/** Placeholder for future CIE-derived support. */
	Cie = "Cie",
}

/** Direction preference for hue interpolation in circular color spaces. */
export enum HueDirection {
	/** Take the shortest angular route around the hue circle. */
	Shortest = "Shortest",
	/** Increase hue around the circle. */
	Clockwise = "Clockwise",
	/** Decrease hue around the circle. */
	CounterClockwise = "CounterClockwise",
}

/** Timing controls for one color path component. */
export interface ColorPathTimingComponent {
	/** Delay as a fraction of the parent cue fade. */
	delay_percent: number;
	/** Active fade duration as a fraction of the parent cue fade. */
	time_percent: number;
}

/** Timing model for a color path transition. */
export interface ColorPathTiming {
	/** Timing for the destination color entering. */
	in_color?: ColorPathTimingComponent;
	/** Timing for the source color leaving. */
	out_color?: ColorPathTimingComponent;
	/** Optional midpoint brightness scale for the interior of the fade. */
	brightness_percent?: number;
	/** Optional timing overrides keyed by color attribute. */
	attributes: Record<string, ColorAttributeTiming>;
}

/** Reusable showfile color path definition. */
export interface ColorPath {
	/** Stable showfile object identity. */
	identifiers: Identifiers;
	/** Interpolation space used by this path. */
	interpolation_space: ColorInterpolationSpace;
	/** Hue route preference when using circular color spaces. */
	hue_direction: HueDirection;
	/** Path timing controls. */
	timing: ColorPathTiming;
	/** Default curve for the path sample. */
	curve: FadeCurve;
}

/** Default color path assignment for a fixture or fixture element. */
export interface ColorPathDefault {
	/** Fixture or fixture element that receives the default color path. */
	fixture: FixtureRef;
	/** Color path used when a cue instruction has no explicit color path. */
	color_path_id: ColorPathId;
}

/** Normalized RGB color used by color path sampling. */
export interface ColorPathRgb {
	/** Red component in the inclusive 0.0-1.0 range. */
	red: number;
	/** Green component in the inclusive 0.0-1.0 range. */
	green: number;
	/** Blue component in the inclusive 0.0-1.0 range. */
	blue: number;
}

/** Structured failure safe to return to the command's initiating client. */
export interface CommandError {
	/** Stable machine-readable code suitable for client-side branching. */
	code: string;
	/** Concise operator-facing explanation of the failure. */
	message: string;
	/** Optional structured domain details that are safe to expose to clients. */
	details?: unknown;
}

/** Severity of non-terminal operator feedback for an active command. */
export enum NoticeLevel {
	/** Informational progress or context. */
	Info = "Info",
	/** A recoverable concern that does not terminate the command. */
	Warning = "Warning",
}

/** Non-terminal operator feedback associated with an active command. */
export interface CommandNotice {
	/** Active command receiving this notice. */
	command_id: CommandId;
	/** Severity used when presenting the notice. */
	level: NoticeLevel;
	/** Operator-facing feedback. */
	message: string;
}

/** Domain-owned success data erased only at the generic response boundary. */
export interface CommandOutput {
	/** Serialized domain result interpreted by the command's typed client. */
	value: unknown;
}

/** Terminal semantic outcome of one accepted command. */
export type CommandOutcome = 
	/** All work required by the command finished successfully. */
	| { type: "Succeeded", data: {
	/** Optional typed domain result erased for transport. */
	output?: CommandOutput;
}}
	/** The requested outcome did not complete. */
	| { type: "Failed", data: CommandError };

/** The single terminal response emitted for one accepted command. */
export interface CommandResult {
	/** Identity of the command whose lifecycle finished. */
	command_id: CommandId;
	/** Terminal success or failure after all required work completed. */
	outcome: CommandOutcome;
}

/** Console DMX address mapping for a fixture. */
export interface ConsoleDmxAddress {
	/** Console universe. */
	universe: number;
	/** Console address. */
	address: number;
}

/** Persisted arguments for externally-driven control actions. */
export interface ControlActionArguments {
	/** One-based control index in the backend-owned control bank. */
	control_index: number;
}

/** Serializable snapshot of current control bank state. */
export interface ControlSnapshot {
	index: number;
	assigned_clip_id?: number;
	assigned_master_id?: number;
	hardware_value: number;
	console_value: number;
	display_value: number;
	is_grabbed: boolean;
	is_clip_active: boolean;
	attached_instance_id?: InstanceId;
}

/** Defines how a cue will be triggered. */
export type CueTriggerType = 
	/** Cue will advance only on a "Go" command to the associated clip. */
	| { type: "Manual", data?: undefined }
	/** Cue will advance when all delays+fades of previous cue are completed. */
	| { type: "FollowPrevious", data?: undefined }
	/**
	 * Cue will advance a predetermined amount of time after the previous cue's
	 * start (delay tracks immediately after prior cue start, regardless of its
	 * fade/delay configuration).
	 */
	| { type: "AfterDelay", data: Duration }
	/**
	 * Cue will advance at a predetermined amount of time after the sequence
	 * starts, regardless of earlier cue timing.
	 */
	| { type: "At", data: Duration };

/**
 * Bit flags that determine which types attributes are tracked. Multiple values
 * can be combined simultaneously.
 */
export enum TrackingFlags {
	/** Enables tracking for HTP channels (generally, just dimmers) */
	HTP = "HTP",
	/** Enables tracking for LTP channels */
	LTP = "LTP",
	/** Enables tracking for FX */
	FX = "FX",
}

/** Nested cue-like component owned by a parent cue. */
export interface CuePart {
	/** Identifiers for the cue part. Part IDs are scoped to their parent cue. */
	identifiers: Identifiers;
	/** Default transition that applies to all attributes in this part */
	transitions: PartialTransition;
	/** Per-transition attribute that override the part default transitions */
	transitions_by_attribute: AttributeTransitions;
	/** Instructions for the part. Transitions on an instruction override part-wide transitions. */
	instructions: BoundCueInstruction[];
	/** Bit flags that determine which types attributes are tracked. */
	tracking_flags: TrackingFlags;
	/** Whether this cue part may pre-position dark fixtures for its position assertions. */
	lookahead?: boolean;
}

/** Tracking behavior for cue values that can carry into later sequence cues. */
export type TrackingMode = 
	/** Uses the owning sequence's tracking mode. */
	| { type: "Inherit", data?: undefined }
	/** Uses explicit tracking flags for this cue. */
	| { type: "Flags", data: TrackingFlags };

/** Definition of a cue */
export interface Cue {
	/** Identifiers for the cue */
	identifiers: Identifiers;
	/** Defines how *this* cue will be triggered */
	trigger: CueTriggerType;
	/** Default transition that applies to all attributes */
	transitions: PartialTransition;
	/** Per-transition attribute that override the default transitions */
	transitions_by_attribute: AttributeTransitions;
	/** Instructions for the cue. Transitions on an instruction override the cue-wide transitions. */
	instructions: BoundCueInstruction[];
	/** Whether the top-level cue part may pre-position dark fixtures for its position assertions. */
	lookahead?: boolean;
	/** Nested cue parts that fire at the same activation time as this cue. */
	parts?: CuePart[];
	/** Bit flags that determine which types attributes are tracked. Multiple values */
	tracking_flags: TrackingFlags;
	/** Tracking mode used when this cue contributes values to sequence tracking. */
	tracking_mode?: TrackingMode;
}

/** Resolved cue timing information needed by playback planners and UI summaries. */
export interface CueDurationProfile {
	/** Longest delay before a cue value starts asserting. */
	max_delay_in: Duration;
	/** Longest fade used while a cue value asserts. */
	max_fade_in: Duration;
	/** Longest delay before a cue value starts releasing. */
	max_delay_out: Duration;
	/** Longest fade used while a cue value releases. */
	max_fade_out: Duration;
	/** Longest resolved delay-in plus fade-in across asserted values. */
	assertion_duration: Duration;
	/** Longest resolved delay-out plus fade-out across releasable values. */
	release_duration: Duration;
	/** Longest resolved transition span across cue-entry in and out timing. */
	max_transition_duration: Duration;
}

/** Resolved cue timing profile for UI/status surfaces. */
export interface CueDurationProfileMessage {
	/** Cue definition UID. */
	cue_uid: Uuid;
	/** Operator-facing cue ID. */
	cue_id: number;
	/** Resolved duration profile for the cue definition. */
	profile: CueDurationProfile;
}

/** Properties specific to custom objects */
export interface CustomProperties {
	/** Path to the GLTF/GLB model file (user-provided) */
	modelPath: string;
	/** Uniform scale factor for the model */
	scale: number;
	/** Optional material color override (hex color string like "#ff0000") */
	colorOverride?: string;
	/** Optional library object name when sourced from object library */
	libraryObjectName?: string;
	/** Optional object library content version fingerprint */
	libraryObjectVersion?: string;
}

/** Persisted arguments for desk command evaluation actions. */
export interface DeskEvalActionArguments {
	/** Command text evaluated by the desk command parser. */
	command: string;
}

/** Recent network output send failure surfaced to the operator UI. */
export interface NetworkOutputSendFailure {
	/** Protocol that attempted the send. */
	protocol: string;
	/** Output universe that failed. */
	universe: number;
	/** Unicast destination IP when available. */
	destination_ip?: Ipv4Addr;
	/** Stable error category. */
	error_kind: string;
	/** OS or transport error message. */
	message: string;
	/** Number of consecutive frames that hit this failure. */
	count: number;
}

/** Engine performance metrics for UI instrumentation panel */
export interface DeskMetrics {
	/** Frames per second (smoothed) */
	fps?: number;
	/** Frame time in milliseconds (smoothed) */
	frame_time_ms?: number;
	/** Total entity count in the ECS world */
	entity_count?: number;
	/** Time spent in frame pacing in milliseconds */
	framepace_time_ms?: number;
	/** Oversleep duration from frame pacing in milliseconds */
	framepace_oversleep_ms?: number;
	/** Number of active compositor layers */
	active_layers: number;
	/** Number of active DMX universes */
	active_universes: number;
	/** Time spent sending Art-Net universes in milliseconds */
	artnet_send_time_ms?: number;
	/** Number of Art-Net universes sent */
	artnet_universe_count: number;
	/** Time spent sending sACN universes in milliseconds */
	sacn_send_time_ms?: number;
	/** Number of sACN universes sent */
	sacn_universe_count: number;
	/** Time spent building the outbound parameter state snapshot */
	parameter_state_build_ms?: number;
	/** Time spent serializing and broadcasting the parameter state snapshot */
	parameter_state_broadcast_ms?: number;
	/** Time spent building the outbound layer stack snapshot */
	layer_stack_build_ms?: number;
	/** Time spent deriving layer transition-state metadata */
	layer_stack_transition_build_ms?: number;
	/** Time spent serializing and broadcasting the layer stack snapshot */
	layer_stack_broadcast_ms?: number;
	/** Time spent in the scheduled timeline layer generation group */
	timeline_layer_generation_ms?: number;
	/** Time spent updating active timeline positions */
	timeline_update_ms?: number;
	/** Time spent synchronizing timeline audio state */
	timeline_audio_ms?: number;
	/** Time spent materializing timeline lookahead sources */
	timeline_lookahead_sources_ms?: number;
	/** Time spent populating materialized timeline lookahead assertions */
	timeline_lookahead_assertions_ms?: number;
	/** Time spent updating timeline-owned lookahead layers */
	timeline_lookahead_layers_ms?: number;
	/** Time spent processing live timeline actions */
	timeline_actions_ms?: number;
	/** Time spent applying timeline parameter tracks */
	timeline_parameters_ms?: number;
	/** Time spent handling timeline seek reconstruction */
	timeline_seek_ms?: number;
	/** Recent network output send failures */
	network_output_send_failures: NetworkOutputSendFailure[];
}

/** Policy for cue ID renumbering when reordering sequence steps in the UI. */
export enum SequenceReorderRenumberPolicy {
	/** Preserve existing cue IDs after reordering sequence steps. */
	Preserve = "Preserve",
	/** Automatically renumber cues to 1..N in their new order after reordering. */
	AutoRenumber = "AutoRenumber",
	/** Prompt in UI before renumbering after reorder. */
	Prompt = "Prompt",
}

/** Policy for handling operations that flatten programmer selection expressions. */
export enum SelectionFlattenPolicy {
	/** Flatten selections immediately without prompting. */
	Silent = "Silent",
	/** Prompt in UI before running operations that may flatten selections. */
	Prompt = "Prompt",
}

/** Preferred display unit for editable time values in the UI. */
export enum TimeDisplayPreference {
	/** Choose a compact unit based on the duration value. */
	Auto = "Auto",
	/** Always display seconds. */
	Seconds = "Seconds",
	/** Always display milliseconds. */
	Milliseconds = "Milliseconds",
	/** Always display one-beat tempo. */
	Bpm = "Bpm",
	/** Always display frequency. */
	Hertz = "Hertz",
}

/** Position source for timeline insert and paste operations. */
export enum TimelinePlacementPreference {
	/** Place timeline inserts and pastes at the current playhead. */
	Playhead = "Playhead",
	/** Place timeline inserts and pastes at the current timeline cursor when available. */
	Cursor = "Cursor",
}

/** Serialized Dockview panel layout that is named and saved with the showfile. */
export interface StoredPanelLayout {
	/** Whether this layout is shown in the switcher; list order defines its position. */
	shownInSwitcher: boolean;
	/** Stable layout id used by the UI command palette and layout manager. */
	id: string;
	/** User-visible layout name. */
	name: string;
	/** Layout storage format version. */
	version: number;
	/** Opaque Dockview layout payload. */
	layout: unknown;
	/** Panel metadata captured when the layout was stored. */
	panels: StoredPanelLayoutPanel[];
	/** Layout creation timestamp in milliseconds since Unix epoch. */
	createdAt: number;
	/** Last layout update timestamp in milliseconds since Unix epoch. */
	updatedAt: number;
}

/** Desk-wide settings configuration */
export interface DeskSettings {
	/** When true, setting an attribute also updates the programmer selection */
	programmer_auto_select: boolean;
	/** Selected audio output device name (None = system default) */
	audio_device?: string;
	/** Policy for cue renumbering when sequence steps are reordered in the UI */
	sequence_reorder_renumber_policy?: SequenceReorderRenumberPolicy;
	/** Policy for handling operations that flatten programmer selection expressions */
	selection_flatten_policy?: SelectionFlattenPolicy;
	/** Preferred display unit for editable time values in the UI. */
	time_display_preference?: TimeDisplayPreference;
	/** Position source for timeline insert and paste operations. */
	timeline_placement_preference?: TimelinePlacementPreference;
	/** Number of timestamped showfile backups to retain after each save; 0 means unlimited. */
	showfile_backup_retention: number;
	/** Showfile-scoped named UI panel layouts. */
	panel_layouts?: StoredPanelLayout[];
	/** Showfile-scoped active UI panel layout restored when the showfile loads. */
	active_panel_layout?: ActivePanelLayout;
}

/** Reference to a DMX channel by universe and address */
export interface DmxChannelRef {
	/** DMX universe (e.g., 1, 2, 3...) */
	universe: number;
	/** DMX address within universe (1-512) */
	address: number;
}

/** Inclusive DMX range for universes or addresses. */
export interface DmxRange {
	/** Inclusive start value. */
	start: number;
	/** Inclusive end value. */
	end: number;
}

/** Per-element parameter values for a single fixture element in the programmer */
export interface ElementParameterValues {
	/** Contains the attributes for this fixture element */
	parameters: Record<string, ValueSource>;
	/** Live Blueprint provenance for attributes currently won by a referenced row. */
	blueprint_sources: Record<string, OutboundBlueprintValueSource>;
}

/** Timeline-authored owner for a planned playback interval. */
export interface TimelinePlaybackOwner {
	/** Timeline runtime UID that authored this playback. */
	timeline_uid: Uuid;
	/** Timeline track ID that owns the authored item. */
	track_id: string;
	/** Timeline action ID that owns the authored action. */
	action_id: string;
}

/** Source object addressed by a planned playback interval. */
export type PlannedPlaybackSource = 
	/** Cue source. */
	| { type: "Cue", uid: Uuid }
	/** Cue sequence source. */
	| { type: "Sequence", uid: Uuid }
	/** Classic FX source. */
	| { type: "Fx", uid: Uuid }
	/** Step FX source. */
	| { type: "StepFx", uid: Uuid }
	/** Wasm FX module source. */
	| { type: "FxModule", uid: Uuid }
	/** Flow source. */
	| { type: "Flow", uid: Uuid };

/** Coarse lifecycle for a planned playback interval at a target time. */
export enum PlannedPlaybackLifecycle {
	/** Playback is actively asserting source output. */
	Active = "active",
	/** Playback has been released but its release tail is still relevant. */
	Releasing = "releasing",
	/** Playback has completed and is an aggregate no-op at the target. */
	Complete = "complete",
	/** Planner could not determine lifecycle from available context. */
	Unknown = "unknown",
}

/** Evaluated instance state that a materializer can turn into a runtime entity. */
export interface EvaluatedInstanceState {
	/** Timeline action owner for this instance. */
	owner: TimelinePlaybackOwner;
	/** Planned source identity. */
	source: PlannedPlaybackSource;
	/** Source-local playback clock position for evaluation. */
	instance_clock_position: Duration;
	/** Absolute timeline position where playback started. */
	started_at_timeline: Duration;
	/** Absolute timeline position where release started, if any. */
	released_at_timeline?: Duration;
	/** Source-local playback position whose output is the release snapshot, if any. */
	release_snapshot_position?: Duration;
	/** Evaluated lifecycle at the target. */
	lifecycle: PlannedPlaybackLifecycle;
}

/** Reason a planned aggregate is a no-op at the target. */
export enum PlannedNoOpReason {
	/** Playback completed before the target position. */
	CompletedBeforeTarget = "completed_before_target",
	/** Release tail completed before the target position. */
	ReleaseCompletedBeforeTarget = "release_completed_before_target",
	/** Source data was missing and the planner could not materialize state. */
	UnresolvedSource = "unresolved_source",
}

/** Planned no-op aggregate retained for diagnostics and materializer reconciliation. */
export interface PlannedNoOp {
	/** Timeline action owner for the aggregate no-op. */
	owner: TimelinePlaybackOwner;
	/** Reason why this aggregate does not materialize runtime state. */
	reason: PlannedNoOpReason;
}

/** Severity for planner diagnostics. */
export enum TimelinePlannerDiagnosticSeverity {
	/** Informational diagnostic. */
	Info = "info",
	/** Recoverable unsupported or unresolved state. */
	Warning = "warning",
	/** Planner could not produce deterministic state for required input. */
	Error = "error",
}

/** Planner diagnostic with source-agnostic context. */
export interface TimelinePlannerDiagnostic {
	/** Optional timeline owner associated with the diagnostic. */
	owner?: TimelinePlaybackOwner;
	/** Diagnostic severity. */
	severity: TimelinePlannerDiagnosticSeverity;
	/** Stable diagnostic code. */
	code: string;
	/** Human-readable diagnostic detail. */
	message: string;
}

/** Source-agnostic evaluated timeline state consumed by ECS materialization. */
export interface EvaluatedTimelineState {
	/** Target timeline position being evaluated. */
	target_time: Duration;
	/** Evaluated instance states. */
	instances: EvaluatedInstanceState[];
	/** Aggregate no-ops at the target position. */
	no_ops: PlannedNoOp[];
	/** Evaluation diagnostics. */
	diagnostics: TimelinePlannerDiagnostic[];
}

/** Host-owned external control preferences, deliberately excluded from showfiles. */
export interface ExternalControlSettings {
	/** Whether other computers may connect to the backend. */
	enabled: boolean;
	/** Selected interface name, or `None` to listen on all IPv4 interfaces. */
	interface?: string;
}

/** Host control preferences and the native listener's actual runtime state. */
export interface ExternalControlState {
	/** Whether this runtime hosts a native network listener. */
	available: boolean;
	/** Preferences persisted independently from the active showfile. */
	settings: ExternalControlSettings;
	/** Addresses on which the backend is currently listening. */
	listening_addresses: string[];
	/** Binding or persistence failure requiring operator attention. */
	error?: string;
}

/** Built-in physical layouts with defined visualization and DMX element ordering. */
export enum FixtureLayout {
	/** Linear row of independently colored segments. */
	LedBar = "led-bar",
	/** Moving head with a single emitter. */
	MovingHead = "moving-head",
	/** Tilting matrix with RGB pixels and white strobe segments. */
	StrobeMatrix = "strobe-matrix",
	/** Bar with 24 white and 48 RGB segments in hardware wiring order. */
	RgbStrobeBar = "rgb-strobe-bar",
	/** Twelve rotating beams with two decorative twelve-segment strips. */
	RotatingWashBeam = "rotating-wash-beam",
	/** Ten rotating RGBW segments without decorative strips. */
	LinearWashBar = "linear-wash-bar",
}

/**
 * Parameter resolution defines how many adjacent DMX channels should be
 * used to specify this parameter's value, permitting for more precision
 * when needed.
 * 
 * Names are derived from GDTF: https://gdtf-share.com/help/en/help/gdtf_builder/key_dmx.html
 */
export enum DmxValueResolution {
	/** Occupies a single DMX channel (values 0-255) */
	Coarse = "Coarse",
	/** Occupies two adjacent DMX channels (values 0-65,535) */
	Fine = "Fine",
	/** Occupies three adjacent DMX channels (values 0-16,777,215) */
	UltraFine = "UltraFine",
	/** Occupies four adjacent DMX channels (values 0-4,294,967,295) */
	Uber = "Uber",
}

/** Unit used for operator-facing values of a fixture parameter. */
export enum ParameterUnit {
	/** Values are entered and displayed as percentages of the parameter range. */
	Percent = "Percent",
	/** Values are entered and displayed as degrees. */
	Degrees = "Degrees",
}

/** Declares whether a parameter's logical value range is unsigned or signed around zero. */
export enum ParameterValuePolarity {
	/** Values run from zero to the parameter's positive maximum. */
	Unsigned = "Unsigned",
	/** Values run negative-to-positive around a logical midpoint. */
	Signed = "Signed",
}

/** A value to be applied to a parameter */
export type ParameterValue = 
	/** Absolute value, raw DMX to be applied to the parameter */
	| { type: "Absolute", data: {
	value: ParameterDmxValue;
}}
	/** Absolute value, a percentage of the parameter's valid value range */
	| { type: "AbsolutePercent", data: {
	value: Percentage;
}}
	/** Relative value, an offset from the parameter's current raw DMX value */
	| { type: "Relative", data: {
	offset: ParameterDmxValue;
}}
	/** Relative value, a percentage of the parameter's valid value range */
	| { type: "RelativePercent", data: {
	offset: Percentage;
}};

/** Merge strategies for combining parameter values */
export enum MergeStrategy {
	/** Highest Take Priority - the highest value will be used */
	HTP = "HTP",
	/** Last Takes Priority - the most recent value will be used */
	LTP = "LTP",
}

/** Metadata for a parameter. */
export interface ParameterMetadata {
	/** The DMX channel width of this logical parameter */
	resolution: DmxValueResolution;
	/** Which attribute this parameter controls */
	attribute: Attribute;
	/** Unit used for values entered and displayed by operators. */
	native_unit?: ParameterUnit;
	/** Whether this parameter's logical values are unsigned or centered around zero. */
	value_polarity?: ParameterValuePolarity;
	/**
	 * The minimum value of this parameter. The parameter can hold values
	 * outside this bound in individual layers, but final output will be
	 * clamped to this value.
	 */
	min: ParameterDmxValue;
	/**
	 * The maximum value of this parameter. The parameter can hold values
	 * outside this bound in individual layers, but final output will be
	 * clamped to this value.
	 */
	max: ParameterDmxValue;
	/**
	 * Fixed offset applied after merging and before clamping.
	 * Can be specified as a raw DMX value or as a percentage of the parameter's range.
	 */
	offset: ParameterValue;
	/**
	 * Whether low and high values should be inverted upon output. This
	 * can be useful for pan/tilt if a fixture was mounted rotated 180 degrees,
	 * for example.
	 */
	is_inverted: boolean;
	/** Whether this parameter is a 'snap' parameter (not fade-able) */
	is_snap: boolean;
	/** The merge strategy for this parameter */
	merge_type: MergeStrategy;
	/** Whether this parameter should respond to grandmaster intensity */
	use_grandmaster: boolean;
}

/**
 * Elements are controllable sub-components of a fixture, sometimes known as
 * segments or sub-fixtures in other desks.
 * 
 * Most older fixtures only have a single element, but modern LED fixtures and
 * light bars typically have several individually controllable segments.
 */
export interface FixtureElement {
	/** Label for the element */
	label: string;
	/** Parameters available for the element */
	parameters: ParameterMetadata[];
}

/** Physical fixture characteristics from GDTF/OFL profiles. */
export interface FixturePhysical {
	/** Inner beam angle in degrees (GDTF: BeamAngle, OFL: degreesMinMax[0]) */
	beamAngle: number;
	/** Outer beam angle in degrees (GDTF: FieldAngle, OFL: degreesMinMax[1]) */
	fieldAngle: number;
	/** Light output in lumens (GDTF: LuminousFlux, OFL: bulb.lumens) */
	lumens?: number;
	/** Native color temperature in Kelvin (GDTF/OFL: colorTemperature) */
	colorTemperature?: number;
	/** Beam rendering style (GDTF: BeamType) */
	beamType: BeamType;
}

/** 3D position vector for fixture placement. */
export interface PlacementPosition {
	/** X coordinate in meters (stage right is positive) */
	x: number;
	/** Y coordinate in meters (up is positive) */
	y: number;
	/** Z coordinate in meters (downstage is positive) */
	z: number;
}

/** Rotation as Euler angles in degrees. */
export interface PlacementRotation {
	/** Rotation around X axis in degrees (pitch) */
	x: number;
	/** Rotation around Y axis in degrees (yaw) */
	y: number;
	/** Rotation around Z axis in degrees (roll) */
	z: number;
}

/**
 * Per-instance 3D placement in the venue (user-defined, not from fixture profile).
 * Position and rotation define where the fixture exists in world space.
 */
export interface FixturePlacement {
	/** World position in meters */
	position: PlacementPosition;
	/** Base orientation as Euler angles in degrees (not pan/tilt - those come from DMX) */
	rotation: PlacementRotation;
}

/**
 * Logical representation for a physical lighting fixture.
 * 
 * This struct contains user-configured fixture data that is serialized and persisted.
 * Geometry data for 3D visualization is provided separately via `FixtureGeometry`.
 */
export interface Fixture {
	/** Identifiers for the fixture */
	identifiers: Identifiers;
	/** Name of the fixture manufacturer */
	make: string;
	/** Fixture model */
	model: string;
	/** Explicit rendering and element wiring layout, independent of display names. */
	layout?: FixtureLayout;
	/** DMX mode name (used to look up geometry from library) */
	mode: string;
	/** List of controllable elements available for the fixture. */
	elements: FixtureElement[];
	/** Physical beam characteristics for visualizer rendering */
	physical?: FixturePhysical;
	/** 3D position and rotation for visualizer rendering */
	placement?: FixturePlacement;
	/** Optional fixture library content ETag fingerprint */
	library_asset_etag?: string;
}

/**
 * Type of GDTF geometry node.
 * 
 * Determines how the geometry node behaves and what properties it has.
 */
export enum GeometryType {
	/** Basic geometry without special behavior. */
	Generic = "generic",
	/** Articulated geometry with a rotation axis (pan/tilt). */
	Axis = "axis",
	/** Light output geometry (beam emitter). */
	Beam = "beam",
	/** Reference to another geometry (instancing). */
	Reference = "reference",
	/** Beam filter (barn doors, iris). */
	FilterBeam = "filterBeam",
	/** Color filter (color wheels, CMY). */
	FilterColor = "filterColor",
	/** Gobo filter (gobo wheels). */
	FilterGobo = "filterGobo",
	/** Display surface for media. */
	Display = "display",
}

/**
 * 4x4 transformation matrix stored as column-major array.
 * 
 * Compatible with Three.js Matrix4.fromArray() format.
 * Contains rotation, scale, and translation in a single matrix.
 */
export interface Transform {
	/**
	 * Column-major 4x4 matrix elements (16 floats).
	 * Order: [col0_x, col0_y, col0_z, col0_w, col1_x, col1_y, col1_z, col1_w, ...]
	 */
	elements: [number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number];
}

/**
 * GDTF PrimitiveType for fallback geometry generation.
 * 
 * When no mesh file is available, primitives define the basic shape
 * to generate using the model dimensions.
 */
export enum PrimitiveType {
	/** Rectangular box. */
	Cube = "cube",
	/** Cylindrical shape. */
	Cylinder = "cylinder",
	/** Spherical shape. */
	Sphere = "sphere",
	/** Base/mounting plate of fixture. */
	Base = "base",
	/** U-shaped yoke for pan axis. */
	Yoke = "yoke",
	/** Head housing for tilt axis. */
	Head = "head",
	/** Scanner mirror assembly. */
	Scanner = "scanner",
	/** Conventional fixture body (PAR can, fresnel). */
	Conventional = "conventional",
	/** Cable/pigtail. */
	Pigtail = "pigtail",
	/** Base variant 1.1. */
	Base1_1 = "base11",
	/** Scanner variant 1.1. */
	Scanner1_1 = "scanner11",
	/** Conventional variant 1.1. */
	Conventional1_1 = "conventional11",
	/** Undefined or unknown primitive. */
	Undefined = "undefined",
}

/**
 * Model definition for a geometry node.
 * 
 * Contains the model name, primitive type for fallback rendering,
 * dimensions, and optional mesh file reference.
 */
export interface GeometryModel {
	/** Model name from GDTF. */
	name: string;
	/** Primitive type for fallback rendering when mesh is unavailable. */
	primitiveType: PrimitiveType;
	/** Length (depth) of the model in meters. */
	length: number;
	/** Width of the model in meters. */
	width: number;
	/** Height of the model in meters. */
	height: number;
	/** Mesh file name (without extension) if available. */
	meshFile?: string;
}

/**
 * Axis type for articulated geometry nodes.
 * 
 * Determines which axis the geometry rotates around.
 */
export enum AxisType {
	/** Rotation around vertical axis (Y in Three.js). */
	Pan = "pan",
	/** Rotation around horizontal axis (X in Three.js). */
	Tilt = "tilt",
	/** Rotation around forward axis (Z in Three.js). */
	Roll = "roll",
}

/**
 * A node in the fixture's geometry tree.
 * 
 * Represents a single part of the fixture with its transform, model,
 * and relationships to other nodes.
 */
export interface GeometryNode {
	/** Unique name within the fixture (e.g., "Yoke", "Head", "Pixel_1"). */
	name: string;
	/** Type of geometry node. */
	geometryType: GeometryType;
	/** Local transform relative to parent (4x4 matrix). */
	transform: Transform;
	/** Model definition if this node has visual geometry. */
	model?: GeometryModel;
	/** Axis type if this is an articulated node. */
	axis?: AxisType;
	/** Index of parent node in the nodes array (-1 for root). */
	parentIndex: number;
	/** Indices of child nodes in the nodes array. */
	children?: number[];
	/** Element name this node controls (for element-to-geometry mapping). */
	controlledElement?: string;
}

/** Format of a mesh resource file. */
export enum MeshFormat {
	/** GLTF Binary format (.glb). */
	Glb = "glb",
	/** 3D Studio Max format (.3ds). */
	Max3ds = "max3ds",
}

/** Information about an available mesh resource. */
export interface MeshResource {
	/** Format of the mesh file. */
	format: MeshFormat;
	/** Relative path within GDTF archive (e.g., "models/gltf/Body.glb"). */
	path: string;
}

/**
 * Complete geometry tree for a fixture.
 * 
 * Contains all geometry nodes in a flat array with parent/child indices,
 * plus available mesh resources and the source GDTF file path.
 */
export interface FixtureGeometry {
	/** Flat array of nodes (parent indices reference into this array). */
	nodes: GeometryNode[];
	/** Indices of root nodes (typically just one). */
	roots: number[];
	/** Available mesh resources keyed by model name. */
	meshResources?: Record<string, MeshResource>;
	/** Path to source GDTF file for mesh loading. */
	gdtfPath?: string;
}

/** Identifies a fixture in the library by make and model */
export interface FixtureLibraryEntry {
	/** Manufacturer name */
	make: string;
	/** Model name */
	model: string;
}

/** Update to fixture 3D position (all coordinates or individual axis) */
export type FixturePlacementPositionUpdate = 
	/** Set all position coordinates at once */
	| { type: "All", data: PlacementPosition }
	/** Set only X coordinate */
	| { type: "X", data: number }
	/** Set only Y coordinate */
	| { type: "Y", data: number }
	/** Set only Z coordinate */
	| { type: "Z", data: number };

/** Update to fixture 3D rotation (all angles or individual axis) */
export type FixturePlacementRotationUpdate = 
	/** Set all rotation angles at once */
	| { type: "All", data: PlacementRotation }
	/** Set only X rotation (pitch) */
	| { type: "X", data: number }
	/** Set only Y rotation (yaw) */
	| { type: "Y", data: number }
	/** Set only Z rotation (roll) */
	| { type: "Z", data: number };

/** A per-fixture placement update payload for batch placement operations. */
export interface FixturePlacementUpdateEntry {
	/** ID of the fixture */
	id: number;
	/** Position update (None = no change) */
	position?: FixturePlacementPositionUpdate;
	/** Rotation update (None = no change) */
	rotation?: FixturePlacementRotationUpdate;
}

/** A contiguous fixture ID range. */
export interface FixtureRangeExpr {
	/** Start fixture ID (inclusive). */
	start: number;
	/** End fixture ID (inclusive). */
	end: number;
}

/** Error information for a single delta op. */
export interface FlowDeltaError {
	/** Index into FlowDelta.ops, or None for a flow-level error. */
	op_index?: number;
	/** Error message. */
	message: string;
}

/** Acknowledgement for a flow delta. */
export interface FlowAck {
	/** Flow identifier. */
	flow_id: FlowId;
	/** Version the client based changes on. */
	base_version: FlowVersion;
	/** Version after applying ops. */
	applied_version: FlowVersion;
	/** Optional errors per op. */
	errors: FlowDeltaError[];
}

/** RGBAW color value for UI display. */
export interface FlowColor {
	/** Red channel (0.0-1.0). */
	r: number;
	/** Green channel (0.0-1.0). */
	g: number;
	/** Blue channel (0.0-1.0). */
	b: number;
	/** White channel (0.0-1.0). */
	w: number;
}

/** Direction of a port on a node. */
export enum FlowPortDirection {
	/** Input port. */
	Input = "input",
	/** Output port. */
	Output = "output",
}

/** Port type used for validation and UI rendering. */
export enum FlowPortType {
	/** Numeric value (f32). */
	Number = "number",
	/** Integer value (i64). */
	Int = "int",
	/** Boolean value. */
	Bool = "bool",
	/** Color value (RGBW). */
	Color = "color",
	/** Selection expression. */
	Selection = "selection",
	/** Attribute label. */
	Attribute = "attribute",
	/** Waveform definition. */
	Waveform = "waveform",
	/** Waveform kind (shape selector). */
	WaveformKind = "waveform_kind",
	/** Layer output summary. */
	Layer = "layer",
	/**
	 * Trigger/event port used to signal discrete, payload-less events,
	 * represented by a monotonically increasing [`FlowSeq`] value for each
	 * activation.
	 */
	Trigger = "trigger",
	/** Free-form string. */
	String = "string",
}

/** Typed value carried on a port. */
export type FlowValue = 
	/** Floating point value. */
	| { type: "Number", data: number }
	/** Integer value. */
	| { type: "Int", data: number }
	/** Boolean value. */
	| { type: "Bool", data: boolean }
	/** String value. */
	| { type: "String", data: string }
	/** Color value. */
	| { type: "Color", data: FlowColor }
	/** Spatial selection. */
	| { type: "Selection", data: SpatialSelection }
	/** Attribute label (e.g. "Intensity"). */
	| { type: "AttributeLabel", data: string }
	/** Multiple attribute labels. */
	| { type: "AttributeLabels", data: string[] }
	/** Waveform definition. */
	| { type: "Waveform", data: FlowWaveform }
	/** Waveform kind (shape). */
	| { type: "WaveformKind", data: WaveformKind }
	/** Layer summary. */
	| { type: "LayerSummary", data: FlowLayerSummary };

/** An option for an enum-constrained Int port. */
export interface FlowEnumOption {
	/** Integer value for this option. */
	value: number;
	/** Display label for this option. */
	label: string;
}

/** A node's port definition. */
export interface FlowPortDefinition {
	/** Stable port identifier. */
	port_id?: FlowPortId;
	/** UI label for the port. */
	name?: string;
	/** Input or output. */
	direction?: FlowPortDirection;
	/** Port data type. */
	port_type?: FlowPortType;
	/** Whether the port is optional. */
	is_optional?: boolean;
	/** Default value if not connected. */
	default_value?: FlowValue;
	/** For Int ports, optional enum options to display as a dropdown. */
	enum_options?: FlowEnumOption[];
}

/** Position of a node in the UI canvas. */
export interface FlowNodePosition {
	/** X coordinate. */
	x: number;
	/** Y coordinate. */
	y: number;
}

/** A node instance in a flow definition. */
export interface FlowNodeDefinition {
	/** Stable node identifier. */
	node_id: FlowNodeId;
	/** Node kind identifier (e.g. "metronome", "waveform"). */
	kind: string;
	/** Display label. */
	label: string;
	/** Port definitions. */
	ports: FlowPortDefinition[];
	/** Optional UI position. */
	position?: FlowNodePosition;
}

/** Reference to a specific port. */
export interface FlowPortRef {
	/** Node identifier. */
	node_id: FlowNodeId;
	/** Port identifier. */
	port_id: FlowPortId;
}

/** Edge connecting an output port to an input port. */
export interface FlowEdgeDefinition {
	/** Output port reference. */
	from: FlowPortRef;
	/** Input port reference. */
	to: FlowPortRef;
}

/** Stored flow definition with identifiers and versioning. */
export interface FlowDefinition {
	/** Flow identifiers. */
	identifiers: Identifiers;
	/** Definition version. */
	flow_version: FlowVersion;
	/** Nodes in the flow. */
	nodes: FlowNodeDefinition[];
	/** Edges between nodes. */
	edges: FlowEdgeDefinition[];
}

/** Full definition snapshot for a flow. */
export interface FlowDefinitionSnapshot {
	/** Flow identifier. */
	flow_id: FlowId;
	/** Definition version. */
	flow_version: FlowVersion;
	/** Nodes in the flow. */
	nodes: FlowNodeDefinition[];
	/** Edges between nodes. */
	edges: FlowEdgeDefinition[];
}

/** An individual change operation in a FlowDelta. */
export type FlowDeltaOp = 
	/** Add a new node. */
	| { type: "AddNode", data: FlowNodeDefinition }
	/** Update node metadata (label, position). */
	| { type: "UpdateNode", data: FlowNodeUpdate }
	/** Remove a node by id. */
	| { type: "RemoveNode", data: {
	/** The id of the node to remove */
	node_id: FlowNodeId;
}}
	/** Add an edge. */
	| { type: "AddEdge", data: FlowEdgeDefinition }
	/** Remove an edge. */
	| { type: "RemoveEdge", data: FlowEdgeDefinition }
	/** Update a port's configuration. */
	| { type: "UpdatePortConfig", data: FlowPortUpdate };

/** Flow definition delta made of discrete operations. */
export interface FlowDelta {
	/** Flow identifier. */
	flow_id: FlowId;
	/** Base version to apply ops against. */
	base_version: FlowVersion;
	/** Operations to apply. */
	ops: FlowDeltaOp[];
}

/** Helper for edge-specific updates (optional future use). */
export interface FlowEdgeRef {
	/** Output port reference. */
	from: FlowPortRef;
	/** Input port reference. */
	to: FlowPortRef;
}

/** Lightweight layer summary for UI display. */
export interface FlowLayerSummary {
	/** Layer label. */
	label: string;
}

/** Category for a node, used by UI grouping. */
export enum FlowNodeCategory {
	/** Generates values (oscillators, constants, colors). */
	Generator = "generator",
	/** Emits triggers/events. */
	Event = "event",
	/** Issues commands or actions. */
	Action = "action",
	/** Utility nodes (math, routing, switches). */
	Utility = "utility",
}

/** Descriptor for a node kind and its ports. */
export interface FlowNodeDescriptor {
	/** Unique node kind identifier. */
	kind: string;
	/** Display name for UI. */
	label: string;
	/** Category for UI grouping. */
	category: FlowNodeCategory;
	/** Port definitions for this node kind. */
	ports: FlowPortDefinition[];
}

/** Partial update for a node. */
export interface FlowNodeUpdate {
	/** Target node id. */
	node_id: FlowNodeId;
	/** Updated label, if any. */
	label?: string;
	/** Updated position, if any. */
	position?: FlowNodePosition;
}

/** Runtime value for a specific port. */
export interface FlowPortRuntimeValue {
	/** Node id. */
	node_id: FlowNodeId;
	/** Port id. */
	port_id: FlowPortId;
	/** Port version. */
	port_version: FlowPortVersion;
	/** Current value. */
	value: FlowValue;
}

/** Port state held by the runtime. */
export interface FlowPortState {
	/** Port id. */
	port_id: FlowPortId;
	/** Port type. */
	port_type: FlowPortType;
	/** Current value. */
	value: FlowValue;
	/** Port version. */
	version: FlowPortVersion;
}

/** Partial update for a port. */
export interface FlowPortUpdate {
	/** Target node id. */
	node_id: FlowNodeId;
	/** Target port id. */
	port_id: FlowPortId;
	/** Updated port name, if any. */
	name?: string;
	/** Updated default value, if any. */
	default_value?: FlowValue;
}

/** Delta for a single port value. */
export interface FlowPortValueDelta {
	/** Flow identifier. */
	flow_id: FlowId;
	/** Definition version. */
	flow_version: FlowVersion;
	/** Node id. */
	node_id: FlowNodeId;
	/** Port id. */
	port_id: FlowPortId;
	/** Port version. */
	port_version: FlowPortVersion;
	/** Updated value. */
	value: FlowValue;
}

/** Full runtime snapshot for a flow (rarely needed). */
export interface FlowSnapshot {
	/** Flow identifier. */
	flow_id: FlowId;
	/** Definition version. */
	flow_version: FlowVersion;
	/** Port runtime values. */
	ports: FlowPortRuntimeValue[];
}

/** Delta for a Trigger port event. */
export interface FlowTriggerDelta {
	/** Flow identifier. */
	flow_id: FlowId;
	/** Definition version. */
	flow_version: FlowVersion;
	/** Node id. */
	node_id: FlowNodeId;
	/** Port id. */
	port_id: FlowPortId;
	/** Monotonic event sequence. */
	seq: FlowSeq;
	/** Count of events since last update. */
	count: number;
}

/** Trigger state held by the runtime. */
export interface FlowTriggerState {
	/** Event sequence. */
	seq: FlowSeq;
	/** Count of events since last evaluation. */
	count: number;
}

/** Waveform shape kinds. */
export enum WaveformKind {
	/** Sine wave. */
	Sin = "sin",
	/** Triangle wave. */
	Triangle = "triangle",
	/** Sawtooth wave. */
	Sawtooth = "sawtooth",
	/** Square wave. */
	Square = "square",
	/** Pulse wave. */
	Pulse = "pulse",
}

/** Waveform definition used by flow nodes. */
export interface FlowWaveform {
	/** Waveform shape. */
	kind: WaveformKind;
	/** Cycle length in seconds. */
	rate_secs: number;
	/** Amplitude. */
	amplitude: number;
	/** Phase offset in radians. */
	phase: number;
	/** Base value added to the waveform output. */
	base: number;
	/** Duty cycle (0.0-1.0, controls what portion of the cycle the waveform occupies). */
	duty_cycle?: number;
}

/** Definition of an procedural effect (fx) */
export interface Fx {
	/** Identifiers for the fx */
	identifiers: Identifiers;
	/** Spatial selection of fixtures to apply the fx to */
	selection: SpatialSelection;
	/** Map of attributes to animate with which waveform */
	attributes: Record<string, FxWaveform>;
}

/** Canonical timing shared by Step FX tracks unless a lane overrides it. */
export interface StepFxTiming {
	/** Real time represented by one authored beat. */
	beat_duration: Duration;
}

/** Quantization applied while distributing phase across resolved selection indexes. */
export type PhaseGroups = 
	/** Give every non-empty selection index its continuously interpolated phase. */
	| { type: "Automatic", data?: undefined }
	/** Quantize selection indexes into the requested number of phase groups. */
	| { type: "Explicit", data: number };

/** Authored phase waypoints distributed across ordered selection indexes. */
export interface StepFxPhase {
	/** Non-empty unwrapped normalized cycle offsets forming a piecewise-linear envelope. */
	waypoints: number[];
	/** Optional quantization retained for effects authored with explicit phase groups. */
	groups?: PhaseGroups;
}

/** Normalized interval within one step during which its target transition occurs. */
export interface StepFxTransition {
	/** Fraction of the step width held at the previous target before interpolation begins. */
	start: Percentage;
	/** Fraction of the step width at which interpolation reaches the step target. */
	end: Percentage;
}

/** Transition curve types supported by the first-release editor. */
export type CurveType = 
	/** Linear interpolation. */
	| { type: "Linear", data: Linear }
	/** Cubic Bézier interpolation. */
	| { type: "Bezier", data: Bezier }
	/** Immediate transition at the transition window's start. */
	| { type: "Snap", data: Snap };

/** One target and its timing and interpolation shape within an FX track. */
export interface FxStep {
	/** Stable identity independent of the step's current position. */
	uid: string;
	/** Absolute or relative target matching the containing track kind. */
	target: ParameterValue;
	/**
	 * Optional live Blueprint supplying this lane attribute at playback time.
	 * 
	 * `target` remains the last resolved scalar value so referenced steps stay
	 * editable and have a deterministic fallback if the Blueprint disappears.
	 */
	blueprint_uid?: string;
	/** Time from this step's start to the following step's start, in beats. */
	width_beats: number;
	/** Start and end fractions bounding interpolation from the previous target. */
	transition: StepFxTransition;
	/** Curve used during the transition portion of the step. */
	curve: CurveType;
}

/** Ordered absolute or relative contribution for one attribute lane. */
export interface FxTrack {
	/** Authored steps in forward traversal order. */
	steps: FxStep[];
}

/** All authored contributions for one fixture attribute. */
export interface FxLane {
	/** Logical attribute controlled by the lane. */
	attribute: Attribute;
	/** Optional timing replacing the effect-wide beat duration for this lane. */
	timing_override?: StepFxTiming;
	/** Optional phase replacing the effect-wide phase for this lane. */
	phase_override?: StepFxPhase;
	/** Optional absolute contribution track. */
	absolute?: FxTrack;
	/** Optional relative contribution track. */
	relative?: FxTrack;
}

/** Scalar source accepted while creating a Step FX from a command. */
export type StepFxCommandValueSource = 
	/** A scalar value authored directly in the command. */
	| { type: "Direct", data: ParameterValue }
	/** An operator address resolved when the command executes. */
	| { type: "Blueprint", data: {
	/** Mutable numeric ID or label used by the operator. */
	address: BlueprintAddress;
	/** Whether to retain a live reference or copy the current value. */
	resolution: BlueprintResolution;
}};

/** Command-time target step whose scalar source may reference a Blueprint address. */
export interface FxStepDraft {
	/** Stable identity independent of the step's current position. */
	uid: string;
	/** Direct value or Blueprint address supplying the target. */
	target: StepFxCommandValueSource;
	/** Time from this step's start to the following step's start, in beats. */
	width_beats: number;
	/** Interpolation window within the step. */
	transition: StepFxTransition;
	/** Curve used during the transition portion of the step. */
	curve: CurveType;
}

/** Command-time sequence whose Blueprint-backed values have not been resolved. */
export interface FxStepSequenceDraft {
	/** Logical attribute controlled by the resulting lane. */
	attribute: Attribute;
	/** Optional absolute baseline paired with relative targets. */
	base_value?: StepFxCommandValueSource;
	/** Ordered targets forming the lane's dynamic contribution track. */
	steps: FxStepDraft[];
}

/** Waveform parameters for FX. */
export interface FxWaveformParams {
	/** The waveform kind (shape) */
	kind: WaveformKind;
	/** The minimum output value (0-255 DMX) */
	min: ParameterDmxValue;
	/** The maximum output value (0-255 DMX) */
	max: ParameterDmxValue;
	/** The duty cycle (0.0-1.0, for Square/Pulse waves) */
	duty_cycle: number;
}

/** Definition of a waveform with timing and scaling */
export interface FxWaveform {
	/** The waveform parameters (kind, min, max, duty_cycle) */
	params: FxWaveformParams;
	/**
	 * The phase range (start, end) in radians for distributing fixtures.
	 * - (0, 2π): Full waveform distribution (default, current behavior)
	 * - (0, π): Fixtures clustered in first half of waveform
	 * - (0, 4π): Fixtures spread over two complete waveform cycles
	 */
	phase_range: Array<number>;
	/** The duration one complete cycle of the waveform should take */
	rate: Duration;
	/** The width of the waveform (0-100%), scales amplitude around midpoint (127.5). */
	width: Percentage;
	/** Whether the waveform should output relative values instead of absolute values. */
	is_relative: boolean;
}

/** Response for GetFixtureProfile command */
export interface GetFixtureProfileResponse {
	/** Fixture information */
	info: AvailableFixtureInfo;
	/** DMX mode requested for this profile response after applying the default mode fallback. */
	requested_mode?: string;
	/**
	 * Parsed fixture data for the requested mode (for visualization preview).
	 * This is an ephemeral fixture with ID 0 - not stored in the show.
	 */
	fixture?: Fixture;
	/**
	 * Geometry data for the fixture (GDTF only).
	 * Used by the visualizer to render an accurate 3D model.
	 */
	geometry?: FixtureGeometry;
}

/**
 * Metadata for an object in the library.
 * 
 * This is stored in `object.json` within the .robj bundle.
 */
export interface ObjectMetadata {
	/** Name of the object (e.g., "Road Case", "Drum Riser") */
	name: string;
	/** Category for organization (e.g., "Stage", "Rigging", "Props") */
	category?: string;
	/** Optional description */
	description?: string;
	/** Default scale factor (1.0 = model units match real-world meters) */
	scale: number;
	/** Optional author/creator */
	author?: string;
	/** Optional license information */
	license?: string;
	/** Optional tags for searching */
	tags?: string[];
}

/** Response for GetObjectProfile command */
export interface GetObjectProfileResponse {
	/** Object information */
	info: AvailableObjectInfo;
	/** Full metadata */
	metadata: ObjectMetadata;
}

/** Definition of a selection group that stores a selection for later recall */
export interface Group {
	/** Identifiers for the group */
	identifiers: Identifiers;
	/** The stored spatial selection */
	selection: SpatialSelection;
	/** Optional description for the group */
	description: string;
}

/** Integer coordinate assigned to a fixture while resolving spatial selection clauses. */
export interface ProjectedCoord {
	x: number;
	y: number;
	z: number;
}

/** Fixture reference paired with its projected coordinate in a resolved selection. */
export interface IndexedFixture {
	fixture: FixtureRef;
	projected_coord: ProjectedCoord;
}

/** Runtime control factors applied pre-merge. Lives on the instance entity. */
export interface InstanceControls {
	/** Intensity scaling factor (0.0-1.0), multiplicative. Default 1.0. */
	intensity_scale: number;
	/** Operator-authored playback speed multiplier. Default 1.0. */
	rate: number;
	/** Runtime master scale applied on top of the authored rate. Default 1.0. */
	rate_master_scale: number;
}

/** The kind of source that was materialized. */
export enum InstanceKind {
	Cue = "Cue",
	Sequence = "Sequence",
	Fx = "Fx",
	Flow = "Flow",
	Programmer = "Programmer",
}

/** Operator-facing instance category for status displays. */
export enum InstanceDisplayKind {
	Cue = "Cue",
	Sequence = "Sequence",
	Fx = "Fx",
	StepFx = "StepFx",
	FlowFx = "FlowFx",
	ModuleFx = "ModuleFx",
	Programmer = "Programmer",
}

/**
 * References to objects by user-facing ID or UUID.
 * 
 * Numeric IDs are interpreted within the object type. Domains with scoped IDs must reject
 * ambiguous unscoped references; this contract does not imply global numeric uniqueness.
 */
export type ObjectRef = 
	| { type: "ById", data: {
	object_type: ObjectType;
	id: number;
}}
	| { type: "ByUid", data: {
	object_type: ObjectType;
	uid: Uuid;
}};

/** Runtime phase corrections for one Step FX lane's absolute and relative tracks. */
export interface StepFxPreviewTrackPhaseOffsets {
	/** Logical fixture attribute identifying the lane. */
	attribute: Attribute;
	/** Normalized correction subtracted from the absolute track cycle. */
	absolute: number;
	/** Normalized correction subtracted from the relative track cycle. */
	relative: number;
}

/** Backend-authored runtime anchor for one Step FX editor preview session. */
export interface StepFxPreviewPlaybackStatus {
	/** Stable editor session identity used to reject unrelated preview playbacks. */
	session_id: StepFxPreviewSessionId;
	/** Wall-clock epoch when the backend sampled this state. */
	sampled_at_epoch_ms: number;
	/** Effective Step FX elapsed time at the sample epoch. */
	elapsed: Duration;
	/** Effective Step FX elapsed seconds advanced per wall-clock second. */
	elapsed_rate: number;
	/** Continuity corrections retained for each lane contribution after live edits. */
	track_phase_offsets: StepFxPreviewTrackPhaseOffsets[];
}

/** Source-specific runtime position for an active instance. */
export type InstancePosition = 
	/** Cue stack position for sequence instances. */
	| { type: "Sequence", data: {
	/** Sequence source object UUID. */
	sequence_uid: Uuid;
	/** Current 1-based cue position. */
	current_position: number;
	/** Number of cues in the sequence. */
	cue_count: number;
	/** Current cue source object UUID, if a cue is active. */
	current_cue_uid?: Uuid;
	/** Display label for the current cue, if available. */
	current_label?: string;
	/** Number of parts in the current cue. */
	current_part_count: number;
	/** Next 1-based cue position, if the sequence can advance. */
	next_position?: number;
	/** Next cue source object UUID, if a cue can advance. */
	next_cue_uid?: Uuid;
	/** Display label for the next cue, if available. */
	next_label?: string;
	/** Number of parts in the next cue, if the sequence can advance. */
	next_part_count?: number;
	/** Retained cue instances still contributing assertion transitions. */
	retained_cues: InstanceSequenceCueStatus[];
}}
	/** Elapsed runtime for time-based playback sources. */
	| { type: "Time", data: {
	/** Elapsed playback duration after rate scaling where applicable. */
	elapsed: Duration;
}}
	/** Playback has no inspectable position. */
	| { type: "None", data?: undefined };

/** Inspectable runtime status for an active instance. */
export interface InstanceStatus {
	/** Current source-specific position. */
	position: InstancePosition;
	/** Wall-clock timestamp when the currently visible source segment was activated. */
	source_activation_epoch_ms?: number;
	/** Source-local elapsed duration for the currently visible transition segment. */
	transition_elapsed?: Duration;
}

/** Information about an active playback for UI display */
export interface InstanceInfo {
	/** Unique runtime identifier for this playback */
	instance_id: InstanceId;
	/** What kind of playback (Cue, Sequence, Fx, Flow, Programmer) */
	kind: InstanceKind;
	/** Operator-facing playback type label. */
	display_kind: InstanceDisplayKind;
	/** Optional display name */
	name?: string;
	/** Tags associated with this playback */
	tags: string[];
	/** Source object that produced this playback, if known. */
	object_ref?: ObjectRef;
	/** Whether this playback is owned by an editor preview workflow. */
	is_preview: boolean;
	/** Whether this playback is fading out after a stop/release command. */
	is_releasing: boolean;
	/** Whether this playback clock is currently frozen or held at zero rate. */
	is_paused: boolean;
	/** Wall-clock timestamp when this playback began releasing, if applicable. */
	release_epoch_ms?: number;
	/** Wall-clock timestamp when this playback was activated, if available. */
	activation_epoch_ms?: number;
	/** Source-local elapsed duration for the currently visible transition segment. */
	transition_elapsed?: Duration;
	/** Owner component UIDs attached to this playback. */
	owner_uids: Uuid[];
	/** Playback priority resolved from the bound clip, if available. */
	priority?: Priority;
	/** ID of the clip bound to this playback, if any */
	bound_clip_id?: number;
	/** Current intensity scale (0.0-1.0) */
	intensity_scale: number;
	/** Current rate multiplier */
	rate: number;
	/** Runtime master scale applied on top of the current rate. */
	rate_master_scale: number;
	/** Current effective rate after master scaling. */
	effective_rate: number;
	/** Backend-authored clock and phase state for an editor-owned Step FX preview. */
	step_fx_preview?: StepFxPreviewPlaybackStatus;
	/** Source-specific runtime status. */
	status: InstanceStatus;
}

/** Metadata making instances addressable and inspectable. */
export interface InstanceMetadata {
	name?: string;
	kind: InstanceKind;
	display_kind: InstanceDisplayKind;
	tags: string[];
}

/** Runtime options resolved when an instance is materialized. */
export interface InstanceOptions {
	/** Timeline/playback-wide lookahead override. `None` preserves source-local authored behavior. */
	lookahead_enabled?: boolean;
}

/** Runtime status for one retained sequence cue instance. */
export interface InstanceSequenceCueStatus {
	/** 1-based cue position. */
	position: number;
	/** Cue source object UUID. */
	cue_uid: Uuid;
	/** Source-local elapsed duration for this retained cue's assertion transitions. */
	transition_elapsed?: Duration;
}

export interface Instant {
	secs_since_epoch: number;
	nanos_since_epoch: number;
}

/** Failure reported while resolving or invoking a registered action. */
export interface InvocationError {
	/** Stable machine-readable failure code. */
	code: string;
	/** User-presentable description of the failure. */
	message: string;
}

/** Network DMX output protocol. */
export enum NetworkDmxProtocol {
	/** Streaming ACN (E1.31). */
	Sacn = "Sacn",
	/** Art-Net protocol. */
	ArtNet = "ArtNet",
}

/** Network DMX output delivery settings. */
export type NetworkDmxDelivery = 
	/** sACN multicast output. */
	| { type: "SacnMulticast", data?: undefined }
	/** Art-Net broadcast output. */
	| { type: "ArtNetBroadcast", data?: undefined }
	/** Unicast output for sACN or Art-Net. */
	| { type: "Unicast", data: {
	/** Target IP address. */
	ip: Ipv4Addr;
}};

/** One named network DMX output target. */
export interface NetworkDmxOutputTarget {
	/** Command-safe target identifier. */
	id: string;
	/** Output protocol. */
	protocol: NetworkDmxProtocol;
	/** Protocol delivery settings. */
	delivery: NetworkDmxDelivery;
}

/** Showfile-scoped network DMX output target definitions. */
export interface NetworkDmxOutputTargets {
	/** Configured target list. */
	targets: NetworkDmxOutputTarget[];
}

/** One named USB DMX output target. */
export interface UsbDmxOutputTarget {
	/** Command-safe target identifier. */
	id: string;
	/** USB device identifier selected for this target. */
	device: string;
	/** Human-friendly USB device label retained when the device is disconnected. */
	device_label?: string;
}

/** Showfile-scoped USB DMX output target definitions. */
export interface UsbDmxOutputTargets {
	/** Configured target list. */
	targets: UsbDmxOutputTarget[];
}

/** Policy for input signal loss behavior. */
export type InputSignalLossPolicy = 
	/** Keep the last input values until updated again. */
	| { type: "Hold", data?: undefined }
	/** Clear input channels after the configured timeout duration. */
	| { type: "ClearAfterTimeout", data: {
}};

/** Input universe visibility mode. */
export enum InputUniverseVisibilityMode {
	/** Show only external transport inputs. */
	ExternalOnly = "ExternalOnly",
	/** Show all detected transport inputs, including local loopback frames. */
	AllDetected = "AllDetected",
}

/** Showfile-scoped runtime settings for network and USB transports. */
export interface IoRuntimeSettings {
	/** Selected network interface name, or `None` for the system default. */
	network_interface?: string;
	/** Whether network DMX output is allowed to transmit. */
	network_output_enabled: boolean;
	/** Whether network DMX input listeners are allowed to receive frames. */
	network_input_enabled: boolean;
	/** Whether USB DMX output is allowed to transmit. */
	usb_output_enabled: boolean;
	/** Showfile-scoped named network DMX output targets. */
	network_dmx_outputs?: NetworkDmxOutputTargets;
	/** Showfile-scoped named USB DMX output targets. */
	usb_dmx_outputs?: UsbDmxOutputTargets;
	/** Transport input behavior when input signal stops. */
	input_signal_loss_policy?: InputSignalLossPolicy;
	/** Stale input age threshold used for automatic clearing and manual stale release. */
	input_signal_loss_timeout: Duration;
	/** Which transport inputs are surfaced in input views. */
	input_universe_visibility_mode?: InputUniverseVisibilityMode;
}

/** Linear interpolation curve. */
export interface Linear {
}

/** Response for ListAvailableFixtures command */
export interface ListAvailableFixturesResponse {
	/** List of available fixtures */
	fixtures: AvailableFixtureInfo[];
}

/** Response containing the currently available WebAssembly fx modules. */
export interface ListAvailableFxModulesResponse {
	/** Discovered module files sorted by module name. */
	modules: AvailableFxModuleInfo[];
}

/** Response for ListAvailableObjects command */
export interface ListAvailableObjectsResponse {
	/** List of available objects */
	objects: AvailableObjectInfo[];
}

/** Persisted master behavior class. */
export enum MasterKind {
	/** Proportionally limits selected intensity output. */
	InhibitiveIntensity = "InhibitiveIntensity",
	/** Multiplies matching live playback rates. */
	PlaybackRate = "PlaybackRate",
}

/** Persisted target affected by a master. */
export type MasterTarget = 
	/** Fixture scope for intensity masters. */
	| { type: "Fixtures", data: FixtureMasterTarget }
	/** Instance scope for rate masters. */
	| { type: "Instances", data: InstanceMasterTarget };

/** Persisted activation mode for a master. */
export type MasterMode = 
	/** Master always contributes its level. */
	| { type: "AlwaysOn", data?: undefined }
	/** Master contributes only when active. */
	| { type: "Toggle", data: {
	/** Whether the toggle-mode master is active. */
	active: boolean;
}}
	/** Master is bypassed. */
	| { type: "Disabled", data?: undefined };

/** Persistent master data stored in showfiles. */
export interface Master {
	/** Stable showfile identifiers. */
	identifiers: Identifiers;
	/** Master behavior class. */
	kind?: MasterKind;
	/** Fixture or playback scope this master affects. */
	target?: MasterTarget;
	/** Activation behavior. */
	mode?: MasterMode;
	/** Control level in user-facing percent; interpretation depends on the master kind. */
	level_percent: number;
}

/** Runtime link from a clip to its attached instance. */
export interface MaterializedClip {
	/** Console-facing ID of the persistent clip. */
	clip_id: number;
	/** Instance controlled by the clip. */
	attached_instance: InstanceId;
	/** Whether stopping the instance should emit a global release. */
	auto_release_on_stop: boolean;
}

/** MIDI device information sent to the UI */
export interface MidiDevice {
	/** Device name */
	name: string;
}

/** MIDI event information sent to the UI */
export interface MidiLastEvent {
	/** Device that generated the event */
	device: string;
	/** MIDI channel/status byte */
	channel: number;
	/** Note or control number */
	note: number;
	/** Velocity or control value */
	velocity: number;
}

/** A mapping from MIDI input to an action */
export interface MidiMapping {
	/** Name of the MIDI device this mapping applies to */
	device_name: string;
	/** MIDI status byte (channel + message type, e.g., 144 for Note On channel 0) */
	channel: number;
	/** MIDI note or control number */
	note: number;
	/** Optional velocity filter (None = match any velocity) */
	velocity?: number;
	/** Action to trigger when this mapping matches */
	action: ActionReference;
}

/** Network interface information for UI display. */
export interface NetworkInterfaceInfo {
	/** System name of the network interface. */
	name: string;
	/** Human-readable interface name, when provided by the platform. */
	friendly_name?: string;
	/** IPv4 addresses associated with this interface. */
	addresses: string[];
}

/** How the current network interface was resolved. */
export enum CurrentNetworkInterfaceMode {
	/** Use the system default interface. */
	SystemDefault = "SystemDefault",
	/** Use the interface explicitly selected in settings. */
	SelectedInterface = "SelectedInterface",
}

/** Current network interface status for UI display. */
export interface NetworkInterfaceStatus {
	/** The system default interface, if one with IPv4 addressing is available. */
	default_interface?: NetworkInterfaceInfo;
	/** The interface currently selected for use. */
	current_interface?: NetworkInterfaceInfo;
	/** Whether the current interface comes from system default or an explicit selection. */
	current_interface_mode: CurrentNetworkInterfaceMode;
	/** Whether the explicitly selected interface is currently unavailable. */
	selected_interface_missing: boolean;
}

/** Content selected when creating a named show and its initial recoverable draft. */
export interface NewShowfileOptions {
	/** Name of the new showfile folder. */
	name: string;
	/** Populate built-in example fixtures and show objects before creating the draft. */
	includeSampleData: boolean;
}

/** Defines valid object types */
export enum ObjectType {
	Fixture = "Fixture",
	Fx = "Fx",
	StepFx = "StepFx",
	FxModule = "FxModule",
	Flow = "Flow",
	Clip = "Clip",
	Master = "Master",
	Group = "Group",
	Parameter = "Parameter",
	Palette = "Palette",
	Cue = "Cue",
	Sequence = "Sequence",
	Timecode = "Timecode",
	Timeline = "Timeline",
}

/**
 * Persistent identity returned after a reference resolves uniquely in its domain.
 * 
 * Resolution establishes existence at lookup time, not continued existence or playback support.
 */
export interface ObjectIdentity {
	/** Domain namespace that owns the object. */
	object_type: ObjectType;
	/** Persistent identifier preserved when the object's numeric ID changes. */
	uid: Uuid;
}

/** OSC color value (RGBA). */
export interface OscColor {
	/** Red channel. */
	red: number;
	/** Green channel. */
	green: number;
	/** Blue channel. */
	blue: number;
	/** Alpha channel. */
	alpha: number;
}

/**
 * Metadata used by the UI to create or update command-line scrollback entries
 * for externally-triggered eval commands.
 */
export interface OscExternalEval {
	/** Correlation ID shared with the `DeskCommand::Eval` dispatch and final result. */
	correlation_id: Uuid;
	/** Command string dispatched through `DeskCommand::Eval`. */
	command: string;
	/** Human-readable source label shown in CommandLine. */
	source: string;
}

/** OSC argument value captured from a decoded OSC message. */
export type OscType = 
	/** Integer OSC value. */
	| { type: "Int", data: number }
	/** Floating-point OSC value. */
	| { type: "Float", data: number }
	/** Double-precision floating-point OSC value. */
	| { type: "Double", data: number }
	/** 64-bit integer OSC value (serialized as a string for TypeScript compatibility). */
	| { type: "Long", data: string }
	/** String OSC value. */
	| { type: "String", data: string }
	/** Binary OSC value. */
	| { type: "Blob", data: number[] }
	/** Timestamp OSC value. */
	| { type: "Time", data: OscTime }
	/** Character OSC value. */
	| { type: "Char", data: string }
	/** RGBA color OSC value. */
	| { type: "Color", data: OscColor }
	/** MIDI OSC value. */
	| { type: "Midi", data: OscMidiMessage }
	/** Boolean OSC value. */
	| { type: "Bool", data: boolean }
	/** OSC array value. */
	| { type: "Array", data: OscType[] }
	/** Nil OSC value. */
	| { type: "Nil", data?: undefined }
	/** Infinitum OSC value. */
	| { type: "Inf", data?: undefined };

/** Latest OSC event observed by the backend. */
export interface OscLastEvent {
	/** Source socket address (`ip:port`). */
	source: string;
	/** OSC address pattern. */
	address: string;
	/** OSC argument payload. */
	args: OscType[];
}

/** OSC listener status presented to the UI. */
export interface OscListenerStatus {
	/** Whether the UDP listener is currently bound and receiving. */
	is_listening: boolean;
	/** Bound local IP address. */
	bind_address: string;
	/** Bound UDP port. */
	port: number;
}

/** A mapping from OSC source/address/argument criteria to an action. */
export interface OscMapping {
	/** Optional source address filter (`ip:port`). */
	source?: string;
	/** OSC address pattern to match (exact string match). */
	address: string;
	/** Optional argument index to match against (defaults to 0 when `arg_value` is set). */
	arg_index?: number;
	/** Optional argument value to match against. */
	arg_value?: string;
	/** Action to trigger when mapping criteria match. */
	action: ActionReference;
}

/** OSC MIDI message payload. */
export interface OscMidiMessage {
	/** Port identifier. */
	port: number;
	/** Status byte. */
	status: number;
	/** First data byte. */
	data1: number;
	/** Second data byte. */
	data2: number;
}

/** OSC source info for UI display. */
export interface OscSource {
	/** Source socket address (`ip:port`). */
	address: string;
}

/** OSC timestamp value represented as NTP seconds plus fractional units. */
export interface OscTime {
	/** Whole seconds since NTP epoch (1900-01-01). */
	seconds: number;
	/** Fractional part in 2^-32 units. */
	fractional: number;
}

/** One Blueprint's current reverse-dependency descriptions. */
export interface OutboundBlueprintDependency {
	/** Stable Blueprint identity used as the dependency lookup key. */
	blueprint_uid: Uuid;
	/** Authored programmer, cue, and sequence objects retaining the live reference. */
	dependents: string[];
}

/** Operator-facing identity retained alongside a materialized Blueprint value. */
export interface OutboundBlueprintValueSource {
	/** Stable identity used by the authored programmer instruction. */
	blueprint_uid: Uuid;
	/** Current mutable numeric address shown to the operator. */
	blueprint_id: number;
	/** Current mutable label shown to the operator. */
	blueprint_label: string;
	/** Future-looking selector retained by the live application. */
	selector: BlueprintSelector;
}

/** Local outbound clip representation used for UI serialization */
export interface OutboundClipLocal {
	clip: Clip;
	/** Whether this clip has an active playback */
	is_active: boolean;
}

/** DMX input/output mode. */
export enum DmxIoMode {
	Input = "input",
	Output = "output",
}

/** Outbound representation of a DMX universe for WebSocket transmission. */
export interface OutboundDmxUniverse {
	universe_id: number;
	channels: ChannelDmxValue[];
	transports: string[];
	io_mode: DmxIoMode;
	transport?: string;
	frame_age_ms?: number;
	is_stale?: boolean;
	is_self?: boolean;
}

/** Typeshare helper to avoid tuple types */
export interface OutboundElementComputedState {
	/** Unique ID of the fixture this state applies to */
	fixture_uid: Uuid;
	/** Contains the attributes for each fixture element */
	parameters: Array<Record<string, ParameterDmxValue>>;
}

/** Typeshare helper to avoid tuple types */
export interface OutboundElementParameterValues {
	/** Unique ID of the fixture this state applies to */
	fixture_uid: Uuid;
	/** Contains the attributes for each fixture element */
	parameters: Array<Record<string, ParameterValue>>;
}

/** Typeshare helper to avoid tuple types */
export interface OutboundElementTransitionState {
	/** Unique ID of the fixture this state applies to */
	fixture_uid: Uuid;
	/** Contains transition-active flags for each fixture element attribute */
	parameters: Array<Record<string, boolean>>;
}

/** Outbound input contribution to parameter state. */
export interface OutboundInputContribution {
	fixture_uid: Uuid;
	element_index: number;
	attribute: Attribute;
	output_value: ParameterDmxValue;
	transport: string;
	universe_id: number;
	source_address: number;
	frame_age_ms: number;
	is_stale: boolean;
}

/** Wire representation of a single layer's computation information sent to the UI */
export interface OutboundLayerState {
	/** Creator name of the layer (cue / programmer / etc.) */
	creator: string;
	/** Source object that created the layer, if available */
	object_ref?: ObjectRef;
	/** Numeric priority – lower values are merged first */
	priority: Priority;
	/** Whether this layer is fading out before removal */
	is_releasing: boolean;
	/** Current source-specific playback position for playback-backed layers. */
	runtime_position?: InstancePosition;
	/** Asserted absolute parameter values for this layer, in the same shape as `ParameterState` */
	asserted_absolute_values: OutboundElementParameterValues[];
	/** Asserted relative parameter values for this layer, in the same shape as `ParameterState` */
	asserted_relative_values: OutboundElementParameterValues[];
	/** Backend-owned lookahead assertions, such as lookahead values. */
	lookahead_asserted_values: OutboundElementParameterValues[];
	/** Computed parameter values for this layer, in the same shape as `ParameterState` */
	computed_values: OutboundElementComputedState[];
	/** Transition-active flags for computed parameter values in this layer */
	computed_transitioning: OutboundElementTransitionState[];
}

/** Parameter values for one fixture element in the client wire format. */
export interface ParameterState {
	/** Absolute parameter values asserted by objects in the layer stack. */
	absolute: Record<string, ParameterValue>;
	/** Relative parameter values asserted by objects in the layer stack. */
	relative: Record<string, ParameterValue>;
	/** Final computed output values after compositing and fixture processing. */
	output: Record<string, ParameterDmxValue>;
}

/** Parameter values for one fixture in the client wire format. */
export interface OutboundParameterState {
	/** Unique ID of the fixture this state applies to. */
	fixture_uid: Uuid;
	/** Absolute, relative, and output values for each fixture element. */
	parameters: ParameterState[];
}

/** Outbound programmer state for a fixture (contains per-element parameter values) */
export interface OutboundProgrammerElementState {
	/** Unique ID of the fixture this state applies to */
	fixture_uid: Uuid;
	/** Contains the parameter values for each fixture element (indexed by element number, 0-based) */
	parameters: ElementParameterValues[];
}

/** One resolved selection slot, including grouped members produced by projection. */
export interface SelectionIndex {
	index: number;
	invert: boolean;
	members: IndexedFixture[];
}

/** Inclusive extents of the projected coordinates occupied by a resolved selection. */
export interface ProjectionBounds {
	min_x: number;
	max_x: number;
	min_y: number;
	max_y: number;
	min_z: number;
	max_z: number;
}

/** Fully resolved fixture order with optional spatial projection metadata. */
export interface ResolvedSelection {
	canonical: FixtureRef[];
	indexes: SelectionIndex[];
	projection_bounds?: ProjectionBounds;
	invert_attrs?: Attribute[];
}

/** Websocket payload that publishes the programmer's resolved spatial selection. */
export interface OutboundProgrammerSpatialSelection {
	selection: SpatialSelection;
	resolved: ResolvedSelection;
}

/** Timecode tick rate enum */
export enum TimecodeRate {
	/** NTSC standard (23.976 fps) */
	Fps23_976 = "Fps23_976",
	/** Cinema standard (24 FPS) */
	Fps24 = "Fps24",
	/** PAL standard (25 FPS) */
	Fps25 = "Fps25",
	/** NTSC standard (29.97 FPS) */
	Fps29_97 = "Fps29_97",
	/** 30 FPS */
	Fps30 = "Fps30",
	/** 60 FPS */
	Fps60 = "Fps60",
}

/** Source for a timecode */
export enum TimecodeSource {
	/** Timecode generated from an internal source */
	Internal = "Internal",
	/** Extract timecode from MIDI MTC */
	MidiMtc = "MidiMtc",
	/** Extract timecode from a SMPTE LTC audio stream */
	SmpteLtc = "SmpteLtc",
	/** Extract timecode from ArtNet network stream */
	ArtNet = "ArtNet",
}

/**
 * Configuration for an internal timecode generator
 * Timecode value with frame rate and frame count metadata.
 */
export interface Timecode {
	/** Identifiers for the timecode */
	identifiers: Identifiers;
	/** Timecode rate */
	rate: TimecodeRate;
	/** Timecode source */
	source: TimecodeSource;
}

/**
 * State of a timecode generator
 * Playback position, rate, and source status for a timecode clock.
 */
export interface TimecodeState {
	/** The timecode slot ID the state is associated with */
	timecode_id: number;
	/**
	 * Whether the timecode is currently running. Inactive timecodes will not
	 * trigger events in the console.
	 */
	is_active: boolean;
	/** The current position of the timecode, stored as a duration past the start time */
	current_time: Duration;
	/** The start position that will mark this timecode as active */
	start_time?: Duration;
	/** Timecode position after which the timecode will automatically disable */
	end_time?: Duration;
}

/** Websocket payload that publishes local timecode state. */
export interface OutboundTimecodeLocal {
	timecode: Timecode;
	state: TimecodeState;
}

/**
 * Output transport for a patched universe.
 * 
 * FIXME: See if we can make this a typetag or dispatched to avoid this enum
 * being coupled to all output types.
 */
export type OutputTransport = 
	/** No output; fixture is patched but DMX is not sent. */
	| { type: "Disabled", data?: undefined }
	/** Streaming ACN (E1.31). */
	| { type: "Sacn", data: {
	/** Delivery mode (multicast or unicast). */
	mode: SacnDelivery;
}}
	/** uDMX USB interface. */
	| { type: "Udmx", data: {
	/** USB device selector used by the uDMX worker. */
	device: string;
}}
	/** Art-Net protocol. */
	| { type: "ArtNet", data: {
	/** Delivery mode (broadcast or unicast). */
	mode: ArtNetDelivery;
}};

/** Destination for output processing. */
export interface OutputDestination {
	/** Output transport. */
	transport: OutputTransport;
	/** Output universe. */
	universe: number;
	/** Output address. */
	address: number;
}

/** Legacy patch payload for UI compatibility, mapping a parameter to a DMX universe and address. */
export interface Patch {
	/** The ID of the DMX universe to which the parameter is patched */
	universe: number;
	/** The DMX address (1-512) to which the parameter is patched */
	address: number;
	/** The output transport for this patch */
	transport?: OutputTransport;
}

/** Planned release interval for a source-local playback. */
export interface PlannedReleaseInterval {
	/** Absolute timeline position where release started. */
	released_at_timeline: Duration;
	/** Absolute timeline position where release completes, when known. */
	release_completed_at_timeline?: Duration;
	/** Absolute timeline position whose output should be frozen as the release source. */
	source_snapshot_at_timeline: Duration;
}

/** Authored playback-rate change inside a planned playback interval. */
export interface PlannedPlaybackRateChange {
	/** Timeline action owner for this rate change. */
	owner: TimelinePlaybackOwner;
	/** Absolute timeline position where the rate change occurs. */
	timeline_position: Duration;
	/** Source-local playback position where the rate change occurs. */
	playback_position: Duration;
	/** Non-negative playback rate multiplier in millionths. */
	rate_micros: number;
}

/** Source intervention kind authored by a timeline action. */
export type PlannedPlaybackInterventionKind = 
	/** Advance an attached sequence. */
	| { type: "SequenceGo", data?: undefined }
	/** Move an attached sequence backward. */
	| { type: "SequenceBack", data?: undefined }
	/** Jump an attached sequence to a one-based cue position. */
	| { type: "SequenceGotoCue", data: number }
	/** Stop or release the attached playback. */
	| { type: "Stop", data?: undefined };

/** Authored source intervention inside a planned playback interval. */
export interface PlannedPlaybackIntervention {
	/** Timeline action owner for this intervention. */
	owner: TimelinePlaybackOwner;
	/** Absolute timeline position where the intervention occurs. */
	timeline_position: Duration;
	/** Source-local playback position where the intervention occurs. */
	playback_position: Duration;
	/** Intervention kind. */
	kind: PlannedPlaybackInterventionKind;
}

/** Planned playback interval with source identity, ownership, and source-local anchors. */
export interface PlannedPlaybackInterval {
	/** Timeline action owner for this playback. */
	owner: TimelinePlaybackOwner;
	/** Playback source identity. */
	source: PlannedPlaybackSource;
	/** Absolute timeline position where playback started. */
	started_at_timeline: Duration;
	/** Absolute timeline position where playback was stopped, if any. */
	stopped_at_timeline?: Duration;
	/** Source-local position corresponding to `started_at_timeline`. */
	source_local_start: Duration;
	/** Release interval, if the playback is releasing or complete after release. */
	release?: PlannedReleaseInterval;
	/** Authored playback-rate changes inside this interval. */
	rate_changes: PlannedPlaybackRateChange[];
	/** Explicit authored interventions inside this interval. */
	explicit_interventions: PlannedPlaybackIntervention[];
	/** Coarse lifecycle at the plan target. */
	lifecycle: PlannedPlaybackLifecycle;
}

/** Source-agnostic timeline event kind. */
export type PlannedTimelineEventKind = 
	/** Start a source playback. */
	| { type: "Start", data: PlannedPlaybackSource }
	/** Stop a source playback. */
	| { type: "Stop", data: PlannedPlaybackSource }
	/** Apply an intervention to an existing source playback. */
	| { type: "Intervene", data: PlannedPlaybackInterventionKind };

/** Authored timeline event retained for deterministic ordering and diagnostics. */
export interface PlannedTimelineEvent {
	/** Timeline action owner for this event. */
	owner: TimelinePlaybackOwner;
	/** Absolute timeline position for this event. */
	timeline_position: Duration;
	/** Event kind. */
	kind: PlannedTimelineEventKind;
}

/** Describes whether a playback source has a known duration. */
export type PlaybackExtent = 
	/** The playback source has a known finite duration. */
	| { type: "Finite", duration: Duration }
	/** The playback source can continue until an external stop or release. */
	| { type: "Indefinite", duration?: undefined }
	/** The duration cannot be resolved from the currently available planning context. */
	| { type: "Unknown", duration?: undefined };

/** Planner-facing duration summary for a playback source. */
export interface PlaybackDurationProfile {
	/** Duration needed for the source to assert its values after activation. */
	assertion: PlaybackExtent;
	/** Duration needed for the source to release values after stop. */
	release: PlaybackExtent;
	/** Total source duration when it can be resolved directly. */
	total: PlaybackExtent;
}

/**
 * Provenance for a playback clock reconstructed from a planned position.
 * 
 * This source is control metadata used to seed `InstanceClock::source` after
 * materialization. It does not participate in transition interpolation math.
 */
export type PlaybackPositionSource = 
	/** Playback was reconstructed from an external source without richer provenance. */
	| { type: "ExternalPosition", data?: undefined }
	/** Playback was reconstructed from a timeline/timecode position. */
	| { type: "Timeline", data: {
	/** Timeline runtime UID that owns the source position. */
	timeline_uid: Uuid;
	/** Timeline position where this playback began. */
	started_at_timeline: Duration;
}};

/**
 * Transient timing payload for reconstructing playback state from a planner result.
 * 
 * This is a command/materialization boundary DTO, not an owner of runtime timing state.
 * Callers should consume it to seed `InstanceClock` and materialized instance anchors,
 * then let those runtime components own subsequent evaluation.
 * 
 * The stored durations are source-local playback positions. `source` carries the
 * authoritative provenance needed to keep the resulting playback clock synchronized
 * with an external owner such as a timeline/timecode source.
 */
export interface PlaybackReconstructionTiming {
	/** Source-local playback position where the active reconstructed segment began. */
	started_at: Duration;
	/** Source-local playback position being reconstructed. */
	position: Duration;
	/**
	 * Authoritative source that owns the reconstructed playback position.
	 * 
	 * This is provenance for seeding the runtime playback clock, not a transition
	 * interpolation input.
	 */
	source?: PlaybackPositionSource;
}

/** Runtime-independent source preserved until the programmer handler has showfile state. */
export type ProgrammerAttributeSource = 
	/** Direct logical value entered by the operator. */
	| { type: "Direct", data: ValueSource }
	/** Blueprint address and source-local reference behavior. */
	| { type: "Blueprint", data: {
	/** Mutable operator-facing address to resolve at execution. */
	address: BlueprintAddress;
	/** Whether execution retains a UUID reference or direct values. */
	resolution: BlueprintResolution;
}};

/** One ordered logical value operation produced by the shared attribute grammar. */
export interface ProgrammerAttributeOperation {
	/** Attribute, category, or complete Blueprint target. */
	target: BlueprintSelector;
	/** Direct value or unresolved Blueprint source. */
	source: ProgrammerAttributeSource;
}

/** UI payload for removing a cue definition from keyed stores. */
export interface RemovedCueDefinition {
	/** Cue definition UID. */
	uid: Uuid;
}

/** UI payload for removing a sequence definition from keyed stores. */
export interface RemovedSequenceDefinition {
	/** Sequence definition UID. */
	uid: Uuid;
}

/** Component storing resolved output destinations for a fixture or parameter. */
export interface ResolvedOutputDestinations {
	/** Output destinations in evaluation order. */
	destinations: OutputDestination[];
}

/** Identifies the host environment running the engine world. */
export enum RuntimeMode {
	/** Native Nightfall process with platform host services. */
	Native = "Native",
	/** Static browser demo backed by an embedded WebAssembly engine. */
	EmbeddedDemo = "EmbeddedDemo",
}

/** Describes whether showfile persistence is available. */
export enum PersistenceCapability {
	/** Native filesystem-backed showfile persistence. */
	Native = "Native",
	/** No persistence host is attached. */
	Unavailable = "Unavailable",
}

/** Describes access to a native content library. */
export enum LibraryCapability {
	/** Native library discovery and import are available. */
	Native = "Native",
	/** Library discovery and import are unavailable. */
	Unavailable = "Unavailable",
}

/** Describes the dynamic FX-module host attached to the runtime. */
export enum FxModuleCapability {
	/** Native Wasmtime-backed FX modules are available. */
	Native = "Native",
	/** Dynamic FX modules are unavailable. */
	Unavailable = "Unavailable",
}

/** Describes the timeline audio output host attached to the runtime. */
export enum TimelineAudioCapability {
	/** Native timeline audio playback and file management are available. */
	Native = "Native",
	/** Playback is limited to bundled browser-demo media. */
	BundledBrowser = "BundledBrowser",
	/** Timeline audio output is unavailable. */
	Unavailable = "Unavailable",
}

/** Complete capability snapshot authored by the active runtime composition. */
export interface RuntimeCapabilities {
	/** Host environment running the engine. */
	runtime_mode: RuntimeMode;
	/** Showfile persistence support. */
	persistence: PersistenceCapability;
	/** Fixture-library support. */
	fixture_library: LibraryCapability;
	/** Scene-object-library support. */
	object_library: LibraryCapability;
	/** Dynamic FX-module execution support. */
	fx_modules: FxModuleCapability;
	/** Timeline audio output support. */
	timeline_audio: TimelineAudioCapability;
	/** Whether MIDI input is available. */
	midi_input: boolean;
	/** Whether OSC input is available. */
	osc_input: boolean;
	/** Whether Art-Net or sACN input is available. */
	network_dmx_input: boolean;
	/** Whether Art-Net or sACN output is available. */
	network_dmx_output: boolean;
	/** Whether USB DMX output is available. */
	usb_dmx_output: boolean;
	/** Whether incomplete flow authoring and execution are enabled for this process. */
	experimental_flows: boolean;
}

/** Type of scene object */
export enum SceneObjectType {
	/** Structural truss for rigging */
	Truss = "truss",
	/** Audience area with multiple people */
	Audience = "audience",
	/** Generic stage element (riser, platform, curtain, etc.) */
	StageElement = "stageElement",
	/** Custom user-provided 3D model */
	Custom = "custom",
}

/** 3D position vector for scene object placement. */
export interface SceneObjectPosition {
	/** X coordinate in meters (stage right is positive) */
	x: number;
	/** Y coordinate in meters (up is positive) */
	y: number;
	/** Z coordinate in meters (downstage is positive) */
	z: number;
}

/** Rotation as Euler angles in degrees for scene objects. */
export interface SceneObjectRotation {
	/** Rotation around X axis in degrees (pitch) */
	x: number;
	/** Rotation around Y axis in degrees (yaw) */
	y: number;
	/** Rotation around Z axis in degrees (roll) */
	z: number;
}

/** Per-instance 3D placement for scene objects in the venue. */
export interface SceneObjectPlacement {
	/** World position in meters */
	position: SceneObjectPosition;
	/** Base orientation as Euler angles in degrees */
	rotation: SceneObjectRotation;
}

/** Type-specific properties for scene objects */
export type SceneObjectProperties = 
	/** Truss-specific properties */
	| { type: "Truss", data: TrussProperties }
	/** Audience-specific properties */
	| { type: "Audience", data: AudienceProperties }
	/** Stage element properties */
	| { type: "StageElement", data: StageElementProperties }
	/** Custom object properties */
	| { type: "Custom", data: CustomProperties };

/** A non-fixture 3D object in the scene (truss, audience, stage element, etc.) */
export interface SceneObject {
	/** Identifiers for the scene object (id, uid, label) */
	identifiers: Identifiers;
	/** Type of scene object */
	objectType: SceneObjectType;
	/** 3D placement in world space */
	placement: SceneObjectPlacement;
	/** Type-specific properties */
	properties: SceneObjectProperties;
}

/** Delay, fade, and curve settings applied when values enter or leave a state. */
export interface Transition {
	delay_in: TransitionMode;
	fade_in: TransitionMode;
	curve_in: FadeCurve;
	delay_out: TransitionMode;
	fade_out: TransitionMode;
	curve_out: FadeCurve;
}

/** Definition of a sequence */
export interface Sequence {
	/** Identifiers for the sequence */
	identifiers: Identifiers;
	/** Steps in the sequence, where each item is a reference to a cue by unique ID. */
	steps: Array<string>;
	/** Whether the sequence should transition to cue 1 when advancing on the last cue */
	wrap: boolean;
	/** Whether the sequence should release the selection on start */
	release_on_start: boolean;
	/** Built-in cue whose values are asserted when this sequence initializes. */
	setup_cue: Cue;
	/** Built-in cue whose transition timing is used when this sequence releases. */
	release_cue: Cue;
	/** Default transition that applies to all attributes */
	default_timing: Transition;
	/** Tracking mode inherited by cues in this sequence. */
	tracking_mode: TrackingMode;
}

/** Backend-authored lookahead state for one visible sequence editor row. */
export interface SequenceLookaheadRowState {
	/** Cue definition UID for this row. */
	cue_uid: Uuid;
	/** Optional cue part ID. `None` addresses the cue row; `Some(0)` addresses the top-level part. */
	part_id?: number;
	/** Whether this row owns lookahead values. */
	lookahead_enabled: boolean;
	/** Downstream source cue IDs whose lookahead assertions may apply from this cue row. */
	source_cue_ids: number[];
}

/** Backend-authored lookahead projection for one sequence. */
export interface SequenceLookaheadStateMessage {
	/** Sequence definition UID. */
	sequence_uid: Uuid;
	/** Lookahead row state keyed by cue and optional part ID. */
	rows: SequenceLookaheadRowState[];
}

/** Sequence snapshot used for sequence-editor preview. */
export interface SequencePreview {
	/** Sequence definition containing the preview step order and default timing. */
	sequence: Sequence;
	/** Cue snapshots referenced by the preview sequence steps. */
	cues: Cue[];
	/** One-based sequence position to render as the active cue. */
	position: number;
}

/** Conflict policy used when importing one showfile object collection into another. */
export enum ShowfileImportPolicy {
	/** Leave the current collection unchanged. */
	Skip = "Skip",
	/** Add incoming objects that do not already exist in the current collection. */
	Merge = "Merge",
	/** Replace current objects that match incoming objects while retaining unrelated current objects. */
	Replace = "Replace",
	/** Replace the current collection with the incoming collection. */
	Overwrite = "Overwrite",
}

/** Per-object-type options for importing data from a showfile snapshot. */
export interface ShowfileImportOptions {
	/** Optional source showfile path; when omitted, the canonical showfile path is used. */
	path?: string;
	/** Import policy for fixture definitions and patch data. */
	fixtures: ShowfileImportPolicy;
	/** Import policy for global variables. */
	variables: ShowfileImportPolicy;
	/** Import policy for desk settings. */
	settings: ShowfileImportPolicy;
	/** Import policy for input, output, and disabled bindings. */
	bindings: ShowfileImportPolicy;
	/** Import policy for MIDI mappings. */
	midi_mappings: ShowfileImportPolicy;
	/** Import policy for OSC mappings. */
	osc_mappings: ShowfileImportPolicy;
	/** Import policy for scene objects. */
	scene_objects: ShowfileImportPolicy;
	/** Import policy for cues. */
	cues: ShowfileImportPolicy;
	/** Import policy for sequences. */
	sequences: ShowfileImportPolicy;
	/** Import policy for groups. */
	groups: ShowfileImportPolicy;
	/** Import policy for masters. */
	masters: ShowfileImportPolicy;
	/** Import policy for blueprints. */
	blueprints: ShowfileImportPolicy;
	/** Import policy for color path definitions. */
	color_paths: ShowfileImportPolicy;
	/** Import policy for FX definitions. */
	fx: ShowfileImportPolicy;
	/** Import policy for stored FX modules. */
	fx_module: ShowfileImportPolicy;
	/** Import policy for step FX. */
	step_fx: ShowfileImportPolicy;
	/** Import policy for flow definitions. */
	flows: ShowfileImportPolicy;
	/** Import policy for timecodes. */
	timecodes: ShowfileImportPolicy;
	/** Import policy for timelines. */
	timelines: ShowfileImportPolicy;
	/** Import policy for clips. */
	clips: ShowfileImportPolicy;
}

/** Identifies one timestamped backup revision and its owning showfile. */
export interface ShowfileRevisionSelection {
	/** Logical showfile name that owns the backup revision. */
	showfileName: string;
	/** Timestamped backup folder name selected for loading. */
	revisionName: string;
}

/** Options captured at the moment a showfile save is requested. */
export interface ShowfileSaveOptions {
	/** Dockview active layout snapshot to write into the saved showfile. */
	activePanelLayout?: ActivePanelLayout;
}

/** Immediate transition curve. */
export interface Snap {
}

/** Properties specific to stage elements */
export interface StageElementProperties {
	/** Path to the GLTF/GLB model file (relative to assets) */
	modelPath: string;
	/** Uniform scale factor for the model */
	scale: number;
}

/** Playback direction shared by all Step FX lanes. */
export enum FxDirection {
	/** Traverse steps in authored order. */
	Forward = "Forward",
	/** Traverse steps in reverse authored order. */
	Reverse = "Reverse",
	/** Traverse forward and backward without repeating turnaround endpoints. */
	Bounce = "Bounce",
}

/** Optional normalization applied to one authored Step FX pass. */
export type StepFxCycleScale = 
	/** Derive each track's pass beats from its authored step widths. */
	| { type: "Auto", data?: undefined }
	/** Fit one authored pass into this positive number of beats. */
	| { type: "Fixed", data: number };

/** Complete stored Step FX definition. */
export interface StepFx {
	/** Stable object identifiers. */
	identifiers: Identifiers;
	/** Fixture membership, order, grouping, and inversion operations. */
	selection: SpatialSelection;
	/** Canonical effect-wide beat timing. */
	timing: StepFxTiming;
	/** Effect-wide phase defaults. */
	phase: StepFxPhase;
	/** Authored traversal direction. */
	direction: FxDirection;
	/** Optional non-destructive normalization of the complete repeating cycle. */
	cycle_scale: StepFxCycleScale;
	/** Independent attribute lanes. */
	lanes: FxLane[];
}

/** Command-time Step FX definition containing unresolved Blueprint addresses. */
export interface StepFxDraft {
	/** Stable and operator-facing identifiers assigned to the stored effect. */
	identifiers: Identifiers;
	/** Fixture membership, order, grouping, and inversion operations. */
	selection: SpatialSelection;
	/** Canonical effect-wide beat timing. */
	timing: StepFxTiming;
	/** Effect-wide phase defaults. */
	phase: StepFxPhase;
	/** Authored traversal direction. */
	direction: FxDirection;
	/** Optional normalization of the complete repeating cycle. */
	cycle_scale: StepFxCycleScale;
	/** Per-attribute sequences converted into canonical lanes after resolution. */
	sequences: FxStepSequenceDraft[];
}

/** A localized validation problem that prevents storing or previewing a draft. */
export interface StepFxValidationIssue {
	/** Stable model path used by the editor to mark the affected control. */
	path: string;
	/** Concise operator-facing explanation. */
	message: string;
}

/** Stored component FX definition owned by a showfile independently of its runtime host. */
export interface StoredFxModule {
	/** Stable numeric and UUID identifiers for the stored component FX object. */
	identifiers: Identifiers;
	/** Component module name resolved by the active runtime host. */
	module_name: string;
	/** Spatial selection owned by the stored component FX object. */
	selection: SpatialSelection;
	/** Component-owned configuration key/value pairs. */
	config: Record<string, string>;
}

/** Store/update payload for fx module definitions. */
export interface StoredFxModuleRequest {
	/** Target stored object identifiers. */
	identifiers: Identifiers;
	/** Plugin module name resolved under the app data `fx-modules` directory. */
	module_name: string;
	/** Replacement spatial selection, when provided. */
	selection?: SpatialSelection;
	/** Replacement or merged config entries. */
	config: Record<string, string>;
	/** Merge new config into existing config instead of replacing it. */
	merge: boolean;
}

/** How a timeline becomes active for playback and trigger processing. */
export enum TimelineTriggerMode {
	/** Timeline starts only from an explicit timeline start command. */
	Manual = "Manual",
	/** Timeline follows start/stop events from its linked timecode. */
	FollowTimecode = "FollowTimecode",
}

/** How timeline-owned state is handled when seeking the linked timecode. */
export enum TimelineSeekBehavior {
	/** Move the playhead only and leave timeline-owned playback state untouched. */
	MovePlayheadOnly = "MovePlayheadOnly",
	/** Rebuild timeline-owned state from the evaluated timeline plan at the seek target. */
	ReconstructState = "ReconstructState",
}

/** How non-deterministic timeline actions are handled during seek reconstruction. */
export enum TimelineNondeterministicSeekBehavior {
	/** Ignore non-deterministic actions while reconstructing state. */
	Ignore = "Ignore",
	/** Dispatch non-deterministic actions when seeking past them. */
	Dispatch = "Dispatch",
}

/** How timeline-owned state is handled when the linked timecode stops. */
export enum TimelineStopBehavior {
	/** Leave triggered actions and timeline-owned actions as-is. */
	KeepState = "KeepState",
	/** Reset trigger state and release timeline-owned actions. */
	ResetAndReleaseOwnedActions = "ResetAndReleaseOwnedActions",
}

/** Timeline-level policy for playback lookahead. */
export enum TimelineLookaheadMode {
	/** Preserve source-local lookahead behavior without timeline-level preactivation. */
	Inherit = "inherit",
	/** Enable lookahead for all timeline-owned playback. */
	Enabled = "enabled",
	/** Disable lookahead for all timeline-owned playback. */
	Disabled = "disabled",
}

/** A Track contains actions positioned along a timeline */
export interface Track {
	/** Unique identifier for this track */
	id: string;
	/** Display name of the track */
	label: string;
	/** Whether the track is muted */
	muted: boolean;
	/** Whether the track is soloed */
	solo: boolean;
	/** Whether the track's parameters are expanded in the UI */
	expanded: boolean;
	/** The list of actions on this track */
	actions: Action[];
	/** Optional parameters that can be adjusted over time */
	automation_lanes: AutomationLane[];
}

/** A labelled point used for timeline navigation and timing references. */
export interface TimelineMarker {
	/** Unique identifier for this marker. */
	uid: string;
	/** User-facing marker label. */
	label: string;
	/** Marker position in timeline time. */
	time: Duration;
	/** Optional CSS color for timeline display. */
	color?: string;
}

/** A labelled time span used to describe show sections. */
export interface TimelineRegion {
	/** Unique identifier for this region. */
	uid: string;
	/** User-facing region label. */
	label: string;
	/** Region start in timeline time. */
	start: Duration;
	/** Region end in timeline time. */
	end: Duration;
	/** Optional CSS color for timeline display. */
	color?: string;
}

/** Persisted loop range for a timeline. */
export interface TimelineLoopRange {
	/** Loop start in timeline time. */
	start: Duration;
	/** Loop end in timeline time. */
	end: Duration;
	/** Whether UI-side loop behavior is enabled. */
	enabled: boolean;
}

/** Scroll behavior mode for timeline playback */
export enum TimelineScrollMode {
	/** Do not auto-scroll during playback */
	Free = "free",
	/** Keep playhead centered in view */
	Center = "center",
	/** Scroll only when playhead approaches viewport edge */
	Follow = "follow",
}

/**
 * A Timeline connects audio playback with timecode and provides
 * a way to trigger events based on timecode position
 */
export interface Timeline {
	/** Identifiers for the timeline */
	identifiers: Identifiers;
	/** The ID of the timecode this timeline is associated with */
	timecode_uid: string;
	/** The time offset in the timecode where the audio should start */
	timecode_start: Duration;
	/** Path to the audio file */
	audio_path: string;
	/** Whether audio playback should follow this timeline. */
	audio_enabled: boolean;
	/** Optional end duration (if not set, will play until the audio file ends) */
	end_time?: Duration;
	/** How this timeline becomes active. */
	trigger_mode?: TimelineTriggerMode;
	/** How seek commands affect timeline-owned state. */
	seek_behavior?: TimelineSeekBehavior;
	/** How seek reconstruction handles timeline actions that cannot be materialized deterministically. */
	nondeterministic_seek_behavior?: TimelineNondeterministicSeekBehavior;
	/** How stop commands affect timeline-owned state. */
	stop_behavior?: TimelineStopBehavior;
	/** How timeline-owned playback should apply lookahead. */
	lookahead?: TimelineLookaheadMode;
	/** Tracks containing actions and parameters that respond to timecode */
	tracks: Track[];
	/** Labelled marker points for navigation and timing references. */
	markers?: TimelineMarker[];
	/** Labelled region spans for show structure. */
	regions?: TimelineRegion[];
	/** Persisted loop range shared by all panels viewing this timeline. */
	loop_range?: TimelineLoopRange;
	/** BPM when using beat grid */
	bpm: number;
	/** Beats per bar when using beat grid */
	beats_per_bar: number;
	/** Whether beat grid mode is enabled */
	use_beat_grid?: boolean;
	/** Persisted detected beatgrid data. */
	beatgrid?: BeatgridData;
	/** UI scroll behavior mode for timeline playback */
	scroll_mode?: TimelineScrollMode;
}

/** Enabled browser media loop bounds. */
export interface TimelineAudioLoopRange {
	/** Inclusive loop start. */
	start: Duration;
	/** Exclusive loop end. */
	end: Duration;
}

/** Timeline action that prevents a future action from preactivating lookahead. */
export interface TimelineLookaheadActionBlocker {
	/** Timeline track that owns the blocking action. */
	track_id: string;
	/** Blocking timeline action identifier. */
	action_id: string;
}

/** Runtime state describing whether a timeline action can currently preactivate. */
export enum TimelineLookaheadActionStatusKind {
	/** The action is safe to preactivate but is not currently asserting lookahead. */
	Ready = "ready",
	/** The action has at least one assertion in the active timeline lookahead layer. */
	Asserted = "asserted",
	/** An intervening timeline action asserts a fixture needed by this action. */
	BlockedByInterveningFixtureAssertions = "blocked_by_intervening_fixture_assertions",
}

/** Runtime lookahead status for a single timeline action. */
export interface TimelineLookaheadActionStatus {
	/** Timeline definition UID that owns the action. */
	timeline_uid: string;
	/** Timeline track that owns the action. */
	track_id: string;
	/** Timeline action identifier. */
	action_id: string;
	/** Current lookahead status. */
	kind: TimelineLookaheadActionStatusKind;
	/** Intervening timeline actions that currently block this action's lookahead. */
	blocking_actions: TimelineLookaheadActionBlocker[];
}

/** Source-agnostic timeline plan produced before materialization. */
export interface TimelinePlan {
	/** Timeline runtime UID being planned. */
	timeline_uid: Uuid;
	/** Target timeline position being planned. */
	target_time: Duration;
	/** Authored events retained in deterministic order. */
	events: PlannedTimelineEvent[];
	/** Playback intervals relevant to planning and reconciliation. */
	instances: PlannedPlaybackInterval[];
	/** Aggregate no-ops at the target position. */
	no_ops: PlannedNoOp[];
	/** Planner diagnostics. */
	diagnostics: TimelinePlannerDiagnostic[];
}

/** Actions staged by an active recording session for one timeline. */
export interface TimelineRecordingPreview {
	/** User-facing timeline ID. */
	timeline_id: number;
	/** Track that receives recorded actions. */
	target_track_id: string;
	/** Actions captured so far in the active recording session. */
	actions: Action[];
}

/** Runtime recording state for one timeline. */
export interface TimelineRecordingState {
	/** User-facing timeline ID. */
	timeline_id: number;
	/** Whether the timeline is armed for recording. */
	enabled: boolean;
	/** Track that receives recorded actions. */
	target_track_id?: string;
}

/**
 * Type of truss structure
 * Scene-object truss geometry categories supported by the visualizer.
 */
export enum TrussType {
	/** Square/box truss (e.g., 12" box) */
	Box = "box",
	/** Triangular truss */
	Triangle = "triangle",
	/** Ladder/flat truss */
	Ladder = "ladder",
}

/** Properties specific to truss objects */
export interface TrussProperties {
	/** Length of the truss in meters */
	length: number;
	/** Type of truss structure */
	trussType: TrussType;
	/** Outer diameter/width of the truss in meters */
	diameter: number;
}

/** Developer-visible metadata for one undo or redo stack group. */
export interface UndoStackEntryMessage {
	/** Zero-based position in execution order, where 0 is the next group to apply. */
	order: number;
	/** Human-readable description for the grouped operation. */
	description: string;
	/** Number of commands grouped into this undoable operation. */
	entry_count: number;
	/** Age of the grouped operation when this message was emitted. */
	age_ms: number;
	/** Whether this group was preserved from a redo branch by GURQ. */
	is_gurq_preserved: boolean;
	/** Batch ID linking commands that undo or redo together. */
	undo_id: string;
	/** Human-readable descriptions for commands in this group. */
	entry_descriptions: string[];
	/** Correlation IDs linking grouped commands to the original command envelopes. */
	correlation_ids: string[];
}

/** Current state of the undo/redo system for UI display */
export interface UndoStateMessage {
	/** Whether there are operations available to undo */
	can_undo: boolean;
	/** Whether there are operations available to redo */
	can_redo: boolean;
	/** Description of the next undo operation */
	undo_description?: string;
	/** Description of the next redo operation */
	redo_description?: string;
	/** Number of items in the undo stack */
	undo_depth: number;
	/** Number of items in the redo stack */
	redo_depth: number;
	/** Undo stack groups in undo execution order. */
	undo_stack: UndoStackEntryMessage[];
	/** Redo stack groups in redo execution order. */
	redo_stack: UndoStackEntryMessage[];
}

/** Reference to a fixture or its element by user-facing ID */
export interface UnresolvedFixtureRef {
	/** Fixture ID */
	fixture_id: number;
	/** Element index (or None for to select all child elements) */
	element_index?: number;
}

/** Compatible USB DMX device currently visible to the host OS. */
export interface UsbDmxDeviceInfo {
	/** Stable device selector persisted in showfile USB transport mappings. */
	id: string;
	/** Human-readable label for select controls. */
	label: string;
	/** USB vendor ID. */
	vendor_id: number;
	/** USB product ID. */
	product_id: number;
	/** USB manufacturer string, when the device exposes one. */
	manufacturer?: string;
	/** USB product string, when the device exposes one. */
	product?: string;
	/** USB serial number, when the device exposes one. */
	serial_number?: string;
	/** Human-readable physical location, when the OS exposes one. */
	location?: string;
}

/** A single waveform sample result. */
export interface WaveformSample {
	/** Normalized waveform value [0.0, 1.0]. */
	value: number;
}

/** Runtime input supplied by an automation surface. */
export type ActionInput = 
	/** A discrete trigger without a continuously varying value. */
	| { type: "Trigger", data?: undefined }
	/** A normalized scalar in the inclusive range `0.0..=1.0`. */
	| { type: "Scalar", data: number };

/** Backend lifecycle state for process initialization and showfile readiness. */
export enum AppState {
	/** Process startup while plugins register and process-lifetime devices are probed. */
	Initializing = "Initializing",
	/** Initial setup is done and no showfile is loaded. */
	Initialized = "Initialized",
	/** A showfile world is being built and swapped into place. */
	ShowLoading = "ShowLoading",
	/** Showfile data is loaded and the backend may process showfile commands. */
	Ready = "Ready",
}

/** Art-Net delivery mode for a universe. */
export type ArtNetDelivery = 
	/** Broadcast to all nodes on the network. */
	| { type: "Broadcast", data?: undefined }
	/** Unicast to a specific IP address. */
	| { type: "Unicast", data: {
	/** Target IP address. */
	ip: Ipv4Addr;
}};

/** Spatial axis used by selection transforms and visualizer geometry. */
export enum Axis {
	X = "X",
	Y = "Y",
	Z = "Z",
}

/** Binding endpoint reference for patch commands (fixture IDs, transports, console, disabled). */
export type BindingEndpoint = 
	/** Console endpoint (optionally scoped to a universe range and address) */
	| { type: "Console", data: {
	/** Optional universe range */
	universe?: DmxRange;
	/** Optional DMX address */
	address?: number;
}}
	/** Transport endpoint (sACN/Art-Net/uDMX) */
	| { type: "Transport", data: {
	/** Protocol name for input sources or output transport target ID for output targets */
	target: string;
	/** Optional universe range */
	universe?: DmxRange;
	/** Optional DMX address */
	address?: number;
}}
	/** Fixture endpoint (fixture IDs with optional element/param) */
	| { type: "Fixture", data: {
	/** Fixture IDs */
	ids: number[];
	/** Optional element index (1-based) */
	element?: number;
	/** Optional parameter name */
	param?: string;
}}
	/** Disabled endpoint (filter) */
	| { type: "Disabled", data?: undefined };

/** Transport identifier used in binding declarations. */
export enum BindingTransport {
	/** Streaming ACN (E1.31). */
	Sacn = "sacn",
	/** Art-Net protocol. */
	ArtNet = "artnet",
	/** uDMX USB interface. */
	Udmx = "udmx",
}

/** Operator-facing address used to resolve a Blueprint at command execution time. */
export type BlueprintAddress = 
	/** Resolve the Blueprint whose mutable numeric identifier matches exactly. */
	| { type: "Id", data: number }
	/** Resolve a case-insensitive exact label, rejecting ambiguous matches. */
	| { type: "Label", data: string };

/** Commands for blueprint CRUD operations */
export type BlueprintCommand = 
	/** Store or update a blueprint */
	| { type: "StoreBlueprint", data: Blueprint }
	/** Rename a blueprint (change numeric ID) */
	| { type: "RenameBlueprint", data: {
	/** ID of the blueprint */
	id: number;
	/** New ID for the blueprint */
	new_id: number;
}}
	/** Delete a blueprint by ID */
	| { type: "DeleteBlueprint", data: number };

/** Whether applying a Blueprint retains a live relationship or copies its current values. */
export enum BlueprintResolution {
	/** Retain the Blueprint UUID and selector for future materialization. */
	Reference = "Reference",
	/** Resolve matching logical values when the command executes. */
	Absolute = "Absolute",
}

/** Commands for clip assignment, persistence, and runtime control. */
export type ClipCommand = 
	/** Assigns a resolved source to a clip. */
	| { type: "AssignSource", data: {
	/** ID of the clip. */
	clip_id: number;
	/** Resolved source to assign. */
	source: Source;
}}
	/** Resolves a source by numeric object ID and assigns it to a clip. */
	| { type: "AssignSourceById", data: {
	/** ID of the clip. */
	clip_id: number;
	/** Source object to resolve and assign. */
	source: ClipSourceRef;
}}
	/** Clears the source assignment from a clip. */
	| { type: "ClearSource", data: number }
	/** Updates clip-local options. */
	| { type: "UpdateClipOptions", data: {
	/** ID of the clip. */
	clip_id: number;
	/** Options to persist on the clip. */
	options: ClipOptions;
}}
	/** Starts a clip and materializes its playback. */
	| { type: "StartClip", data: IdExpr }
	/** Starts a clip from a reconstruction timing seed. */
	| { type: "StartClipAtTiming", data: {
	/** ID of the clip. */
	clip_id: IdExpr;
	/** Source-local timing used to seed the materialized instance. */
	timing: PlaybackReconstructionTiming;
	/** Optional playback behavior overrides supplied by the caller. */
	instance_options?: InstanceOptions;
}}
	/** Stops a clip and releases its attached playback. */
	| { type: "StopClip", data: IdExpr }
	/** Stops a clip from a release reconstruction timing seed. */
	| { type: "StopClipAtTiming", data: {
	/** ID of the clip. */
	clip_id: IdExpr;
	/** Playback-local timing used to anchor release. */
	timing: PlaybackReconstructionTiming;
}}
	/** Advances attached playback, starting it first when necessary. */
	| { type: "GoClip", data: IdExpr }
	/** Moves attached playback backward. */
	| { type: "BackClip", data: IdExpr }
	/** Sets the rate multiplier for attached playback. */
	| { type: "SetRate", data: {
	/** ID of the clip. */
	clip_id: IdExpr;
	/** Playback clock rate multiplier. */
	rate: number;
}}
	/** Jumps sequence playback to a one-based cue position. */
	| { type: "GotoClip", data: {
	/** ID of the clip. */
	clip_id: IdExpr;
	/** One-based cue position. */
	position: number;
	/** Optional source-local timing used to seed the entered transition. */
	timing?: PlaybackReconstructionTiming;
}}
	/** Renders sequence playback at a planner-derived source-local position. */
	| { type: "RenderClipAt", data: {
	/** ID of the clip. */
	clip_id: IdExpr;
	/** One-based cue position. */
	position: number;
	/** Source-local timing used to seed the active cue and playback clock. */
	timing: PlaybackReconstructionTiming;
	/** Optional playback behavior overrides supplied by the caller. */
	instance_options?: InstanceOptions;
}}
	/** Stores a clip record. */
	| { type: "StoreClip", data: Clip }
	/** Renames a clip's console-facing ID. */
	| { type: "RenameClip", data: {
	/** Existing clip ID. */
	id: number;
	/** Replacement clip ID. */
	new_id: number;
}}
	/** Deletes a clip by console-facing ID. */
	| { type: "DeleteClip", data: number };

/** Clip source references addressed by console-facing numeric ID before resolution. */
export type ClipSourceRef = 
	/** Cue sequence addressed by numeric ID. */
	| { type: "Sequence", data: number }
	/** Parameter effect addressed by numeric ID. */
	| { type: "Fx", data: number }
	/** Flow graph addressed by numeric ID. */
	| { type: "Flow", data: number }
	/** Step effect addressed by numeric ID. */
	| { type: "StepFx", data: number }
	/** Effect module addressed by numeric ID. */
	| { type: "FxModule", data: number };

/** Describes the external surface that accepted a command. */
export type CommandOrigin = 
	/** A command submitted through the Web UI transport. */
	| { type: "WebUi", data?: undefined }
	/** A command submitted through the process command-line interface. */
	| { type: "Cli", data?: undefined }
	/** A command submitted by another named remote-control transport. */
	| { type: "Remote", data: string };

/** Fallible user commands that mutate control assignments. */
export type ControlCommand = 
	| { type: "AssignClip", data: {
	control_index: number;
	clip_id: number;
}}
	| { type: "AssignMaster", data: {
	control_index: number;
	master_id: number;
}}
	| { type: "ClearClip", data: {
	control_index: number;
}};

/** High-frequency control changes that intentionally have no command lifecycle. */
export type ControlUpdate = 
	| { type: "SetConsoleValue", data: {
	control_index: number;
	value: number;
}}
	| { type: "SetHardwareValue", data: {
	control_index: number;
	value: number;
}}
	| { type: "SetExternalHardwareValue", data: {
	control_index: number;
	value: number;
}};

/** Commands for cue-related operations */
export type CueCommand = 
	/** Store a cue definition */
	| { type: "StoreCue", data: Cue }
	/** Store a sequence definition */
	| { type: "StoreSequence", data: Sequence }
	/** Rename a cue (within the same or different sequence) */
	| { type: "RenameCue", data: {
	/** Sequence ID of the cue to rename */
	sequence_id: number;
	/** Cue ID within the sequence */
	cue_id: number;
	/** New sequence ID for the cue */
	new_sequence_id: number;
	/** New cue ID for the cue */
	new_cue_id: number;
}}
	/** Delete a cue from a sequence */
	| { type: "DeleteCue", data: {
	/** Sequence ID containing the cue */
	sequence_id: number;
	/** Cue ID to delete */
	cue_id: number;
}}
	/** Assert values tracked into a cue or cue part. */
	| { type: "BlockCue", data: {
	/** Sequence ID containing the target cue. */
	sequence_id: number;
	/** Cue ID to block. */
	cue_id: number;
	/** Optional cue part ID to block instead of the main cue. */
	part_id?: number;
	/** Whether existing target-cue assertions should be replaced by tracked values. */
	overwrite: boolean;
}}
	/** Remove assertions that still match values tracked into a cue or cue part. */
	| { type: "UnblockCue", data: {
	/** Sequence ID containing the target cue. */
	sequence_id: number;
	/** Cue ID to unblock. */
	cue_id: number;
	/** Optional cue part ID to unblock instead of the main cue. */
	part_id?: number;
}}
	/** Set or clear the color path for supported color-vector instructions in a cue. */
	| { type: "SetCueColorPath", data: {
	/** Sequence ID containing the cue. */
	sequence_id: number;
	/** Cue ID within the sequence. */
	cue_id: number;
	/** Color path ID to assign, or none to clear the assignment. */
	color_path_id?: ColorPathId;
}}
	/** Store a color path definition. */
	| { type: "StoreColorPath", data: ColorPath }
	/** Update a color path label by numeric ID. */
	| { type: "LabelColorPath", data: {
	/** ID of the color path to update. */
	id: number;
	/** New label for the color path. */
	label: string;
}}
	/** Duplicate a color path definition into a new numeric ID. */
	| { type: "DuplicateColorPath", data: {
	/** ID of the source color path. */
	id: number;
	/** ID to assign to the duplicated color path. */
	new_id: number;
}}
	/** Rename a color path definition to a new numeric ID. */
	| { type: "RenameColorPath", data: {
	/** ID of the color path to rename. */
	id: number;
	/** New ID for the color path. */
	new_id: number;
}}
	/** List known color path definitions. */
	| { type: "ListColorPaths", data?: undefined }
	/** Delete a color path definition by numeric ID. */
	| { type: "DeleteColorPath", data: number }
	/** Rename a sequence */
	| { type: "RenameSequence", data: {
	/** ID of the sequence */
	id: number;
	/** New ID for the sequence */
	new_id: number;
}}
	/** Delete a sequence */
	| { type: "DeleteSequence", data: number };

/** Commands for cue preview during editing */
export type CuePreviewCommand = 
	/** Preview a cue configuration. */
	| { type: "PreviewCue", data: {
	/** Existing preview instance to replace, or `None` to start a new one. */
	instance_id?: InstanceId;
	/** Cue snapshot to materialize. */
	cue: Cue;
}}
	/** Preview a sequence snapshot at a cue position. */
	| { type: "PreviewSequence", data: {
	/** Existing preview instance to replace, or `None` to start a new one. */
	instance_id?: InstanceId;
	/** Sequence snapshot to materialize. */
	preview: SequencePreview;
}}
	/** Stop the preview instance with the given runtime instance ID. */
	| { type: "StopPreview", data: {
	/** Playback to release. */
	instance_id: InstanceId;
}}
	/** Assigns transition-local preview time and whether playback should continue. */
	| { type: "SeekPreview", data: {
	/** Editor-owned preview instance to control. */
	instance_id: InstanceId;
	/** Elapsed time since the active cue was asserted. */
	position: Duration;
	/** Whether to continue playback from the assigned time. */
	playing: boolean;
}};

/** Wrapper for serializing cue lists with WsOutbound-compatible format */
export type CueWsMessage = 
	/** List of all cues */
	| { type: "CueDefinitions", data: Cue[] }
	/** One committed cue definition update. */
	| { type: "CueDefinitionUpdated", data: Cue }
	/** One committed cue definition removal. */
	| { type: "CueDefinitionRemoved", data: RemovedCueDefinition }
	/** List of all sequences */
	| { type: "SequenceDefinitions", data: Sequence[] }
	/** One committed sequence definition update. */
	| { type: "SequenceDefinitionUpdated", data: Sequence }
	/** One committed sequence definition removal. */
	| { type: "SequenceDefinitionRemoved", data: RemovedSequenceDefinition }
	/** List of all color path definitions */
	| { type: "ColorPathDefinitions", data: ColorPath[] }
	/** Resolved timing profiles for all cues */
	| { type: "CueDurationProfiles", data: CueDurationProfileMessage[] }
	/** Resolved timing profile for one committed cue definition update. */
	| { type: "CueDurationProfileUpdated", data: CueDurationProfileMessage }
	/** Timing profile removal for one removed cue definition. */
	| { type: "CueDurationProfileRemoved", data: RemovedCueDefinition }
	/** Backend-authored lookahead projections for all sequences */
	| { type: "SequenceLookaheadStates", data: SequenceLookaheadStateMessage[] }
	/** A cue command for reactive UI handling */
	| { type: "CueCommand", data: CueCommand };

/**
 * Events representing commands for the engine
 * Desk-level commands accepted from the UI and command parser.
 */
export type DeskCommand = 
	/** Quit the application */
	| { type: "Quit", data?: undefined }
	/** Create a fresh unnamed empty showfile. */
	| { type: "NewShowfile", data?: undefined }
	/** Create a fresh showfile for a named `.nightfall-show` folder. */
	| { type: "NewNamedShowfile", data: NewShowfileOptions }
	/** Save showfile */
	| { type: "SaveShowfile", data: ShowfileSaveOptions }
	/** Save showfile to a named `.nightfall-show` folder. */
	| { type: "SaveNamedShowfile", data: {
	/** Target showfile folder name. */
	name: string;
	/** Save-time options captured by the caller. */
	options?: ShowfileSaveOptions;
}}
	/** Save a recoverable draft for the current showfile when it is dirty. */
	| { type: "SaveDraftShowfile", data: ShowfileSaveOptions }
	/** Save a recoverable draft for a named `.nightfall-show` folder. */
	| { type: "SaveNamedDraftShowfile", data: {
	/** Target showfile folder name. */
	name: string;
	/** Save-time options captured by the caller. */
	options?: ShowfileSaveOptions;
}}
	/** Load showfile */
	| { type: "LoadShowfile", data?: undefined }
	/** Load a named `.nightfall-show` folder. */
	| { type: "LoadNamedShowfile", data: string }
	/** Load a draft from the app data drafts folder. */
	| { type: "LoadDraftShowfile", data: string }
	/** Load a backup revision into working state without replacing the saved showfile. */
	| { type: "LoadShowfileRevision", data: ShowfileRevisionSelection }
	/** Delete a draft from the app data drafts folder. */
	| { type: "DiscardDraftShowfile", data: string }
	/** Import selected object collections from a showfile */
	| { type: "ImportShowfile", data: ShowfileImportOptions }
	/** Set log level filter (e.g. "debug", "nightfall=trace") */
	| { type: "SetLogLevel", data: string }
	/** Trace outputs related to a specific fixture attribute */
	| { type: "TraceFixture", data: {
	fixture_ref: UnresolvedFixtureRef;
	attribute: Attribute;
}}
	/** Set a log tracing filters (filter uses same format as those of RUST_LOG) */
	| { type: "SetTracingFilter", data: {
	field: string;
	value?: string;
}}
	/** Clear all existing log tracing filters */
	| { type: "ClearTracingFilter", data?: undefined }
	/** Clear transport input assertions whose source universes are stale. */
	| { type: "ReleaseStaleInputs", data?: undefined }
	/** Parse a command and generate domain-owned commands. */
	| { type: "Eval", data: string }
	/** Sleep for a duration before processing subsequent commands */
	| { type: "Sleep", data: Duration }
	/** Release an active object */
	| { type: "Release", data: ObjectRef };

/** Wrapper for serializing cue lists with WsOutbound-compatible format */
export type DeskWsMessage = 
	/** Definition of all groups */
	| { type: "GroupDefinitions", data: Group[] }
	/** Definition of all masters */
	| { type: "MasterDefinitions", data: Master[] }
	/** Definition of all clips */
	| { type: "ClipDefinitions", data: OutboundClipLocal[] }
	/** Definition of all blueprints */
	| { type: "BlueprintDefinitions", data: Blueprint[] }
	/** Reverse dependencies for every Blueprint with a live authored reference. */
	| { type: "BlueprintDependencies", data: OutboundBlueprintDependency[] }
	/** Current desk settings */
	| { type: "Settings", data: DeskSettings }
	/** Current IO runtime settings */
	| { type: "IoSettings", data: IoRuntimeSettings }
	/** Host-owned external control settings and listener status. */
	| { type: "ExternalControlState", data: ExternalControlState }
	/** Network interfaces list */
	| { type: "AvailableNetworkInterfaces", data: NetworkInterfaceInfo[] }
	/** Current/default network interface status */
	| { type: "NetworkInterfaceStatus", data: NetworkInterfaceStatus }
	/** Audio devices list (map of stable id -> display name) */
	| { type: "AvailableAudioDevices", data: Record<string, string> }
	/** Compatible USB DMX devices list */
	| { type: "AvailableUsbDmxDevices", data: UsbDmxDeviceInfo[] }
	/** A desk command */
	| { type: "DeskCommand", data: DeskCommand }
	/** UI-only notification */
	| { type: "UiNotification", data: UiNotification }
	/** A group command */
	| { type: "GroupCommand", data: GroupCommand }
	/** An clip command */
	| { type: "ClipCommand", data: ClipCommand }
	/** A blueprint command */
	| { type: "BlueprintCommand", data: BlueprintCommand }
	/** Layer stack with computed values (sorted by priority) */
	| { type: "LayerStack", data: OutboundLayerState[] }
	/** Engine performance metrics */
	| { type: "Metrics", data: DeskMetrics }
	/** Active instances list */
	| { type: "ActiveInstances", data: InstanceInfo[] }
	/** Backend-owned control state */
	| { type: "Controls", data: ControlSnapshot[] }
	/** Current undo/redo state */
	| { type: "UndoState", data: UndoStateMessage };

/** Expression for selecting DMX channels (similar to IdExpr but for DMX addresses) */
export type DmxChannelExpr = 
	/** A single DMX channel */
	| { type: "Single", data: DmxChannelRef }
	/** A range of DMX channels (start thru end, inclusive) */
	| { type: "Range", data: {
	/** Start channel */
	start: DmxChannelRef;
	/** End channel */
	end: DmxChannelRef;
}}
	/** Addition of two expressions */
	| { type: "Add", data: {
	/** Left-hand side of the addition */
	lhs: DmxChannelExpr;
	/** Right-hand side of the addition */
	rhs: DmxChannelExpr;
}}
	/** Subtraction of two expressions */
	| { type: "Sub", data: {
	/** Left-hand side of the subtraction */
	lhs: DmxChannelExpr;
	/** Right-hand side of the subtraction */
	rhs: DmxChannelExpr;
}}
	/** Parenthesized expression */
	| { type: "Span", data: DmxChannelExpr };

/** Duration input accepted by command and editor conversion helpers. */
export type DurationInput = 
	/** Seconds per beat. */
	| { type: "Seconds", data: number }
	/** Beats per minute. */
	| { type: "Bpm", data: number }
	/** Beats per second. */
	| { type: "Hz", data: number }
	/** Milliseconds per beat. */
	| { type: "Milliseconds", data: number }
	/** Fraction of a one-second timing context. */
	| { type: "Percentage", data: Percentage };

/** Element selector applied to fixtures. */
export type ElementSelectorExpr = 
	/** Single element index (1-based). */
	| { type: "Single", data: number }
	/** Range of element indices (inclusive). */
	| { type: "Range", data: {
	start: number;
	end: number;
}};

/**
 * Wrapper for serializing engine-level client messages.
 * 
 * This enum follows the standard `{type, data}` discriminated union format
 * used throughout the application for client messages.
 */
export type EngineClientMessage = 
	/** Server version sent on client connection */
	| { type: "ServerVersion", data: string }
	/** Current backend lifecycle state */
	| { type: "AppState", data: AppState }
	/** Canonical fixture attribute presentation metadata */
	| { type: "AttributeMetadata", data: AttributeMetadata[] }
	/** Capabilities supplied by the active engine host and runtime policy. */
	| { type: "RuntimeCapabilities", data: RuntimeCapabilities }
	/** Result of a command (success or error with correlation ID) */
	| { type: "CommandResult", data: CommandResult }
	/** Non-terminal operator feedback for an active command. */
	| { type: "CommandNotice", data: CommandNotice }
	/** Notification that all resync handlers published their current state. */
	| { type: "ResyncComplete", data?: undefined };

/** User-facing commands owned by engine infrastructure. */
export type EngineCommand = 
	/** Request that all plugins resend their full state */
	| { type: "ResyncState", data?: undefined }
	/** Set the FPS upper-bound */
	| { type: "SetFps", data: number };

/** Commands for fixture-related operations */
export type FixtureCommand = 
	/** Store a fixture */
	| { type: "StoreFixture", data: Fixture }
	/** Rename a fixture */
	| { type: "RenameFixture", data: {
	/** ID of the fixture */
	id: number;
	/** New ID for the fixture */
	new_id: number;
}}
	/** Delete a fixture */
	| { type: "DeleteFixture", data: number }
	/** Set raw DMX channel values */
	| { type: "SetDmxChannels", data: {
	/** DMX channel selection expression */
	channels: DmxChannelExpr;
	/** Raw DMX value (0-255) */
	value: ChannelDmxValue;
}}
	/** Update placement for multiple fixtures in one command. */
	| { type: "UpdateFixturePlacements", data: {
	/** Per-fixture updates to apply. */
	updates: FixturePlacementUpdateEntry[];
}}
	/** Update fixture patch (universe/address/transport) */
	| { type: "UpdateFixturePatch", data: {
	/** ID of the fixture */
	id: number;
	/** DMX universe (1-based) */
	universe: number;
	/** DMX start address (1-512) */
	address: number;
	/** Optional transport override (None = keep current) */
	transport?: OutputTransport;
}}
	/** Add a patch binding (input/output/disabled) */
	| { type: "PatchBinding", data: {
	/** Binding source endpoint */
	source: BindingEndpoint;
	/** Binding target endpoint */
	target: BindingEndpoint;
	/** Binding priority (lower runs first) */
	priority: number;
	/** Clone source address across target range */
	clone: boolean;
}}
	/** Remove patch bindings matching the filter */
	| { type: "RemovePatchBinding", data: {
	/** Optional source filter */
	source?: BindingEndpoint;
	/** Optional target filter */
	target?: BindingEndpoint;
	/** Optional priority filter */
	priority?: number;
	/** Optional clone filter */
	clone?: boolean;
}}
	/** Update fixture parameter metadata offset */
	| { type: "UpdateFixtureParameterOffset", data: {
	/** ID of the fixture */
	id: number;
	/** Parameter attribute to update */
	attribute: Attribute;
	/** New offset value (raw DMX or percentage of range) */
	offset: ParameterValue;
}}
	/** Set or clear the default color path for a fixture or fixture element. */
	| { type: "SetColorPathDefault", data: {
	/** Fixture or fixture element receiving the default. */
	fixture: FixtureRef;
	/** Color path ID to assign, or None to clear the default. */
	color_path_id?: ColorPathId;
}}
	/** Set or clear the default color path for a fixture or fixture element by fixture ID. */
	| { type: "SetColorPathDefaultById", data: {
	/** Fixture ID receiving the default. */
	id: number;
	/** Optional element index receiving the default. */
	element_index?: number;
	/** Color path ID to assign, or None to clear the default. */
	color_path_id?: ColorPathId;
}};

/** Commands for fixture library operations */
export type FixtureLibraryCommand = 
	/** List all available fixtures in the library */
	| { type: "ListAvailableFixtures", data?: undefined }
	/** Get detailed information about a specific fixture */
	| { type: "GetFixtureProfile", data: {
	/** Manufacturer name */
	make: string;
	/** Model name */
	model: string;
	/** Optional DMX mode name (if not provided, uses first/default mode) */
	mode?: string;
}}
	/** Refresh the fixture library by rescanning the directory */
	| { type: "RefreshLibrary", data?: undefined }
	/** Create a fixture from the library */
	| { type: "CreateFixtureFromLibrary", data: {
	/** Fixture ID */
	id: number;
	/** Manufacturer name */
	make: string;
	/** Model name */
	model: string;
	/** DMX mode name */
	mode: string;
	/** Optional user-defined label for the fixture */
	label?: string;
	/** Existing fixture IDs to update to this library asset version */
	update_existing_ids?: number[];
	/** Update existing fixtures without creating a new fixture instance. */
	update_existing_only?: boolean;
}}
	/** Upload a fixture file to the library */
	| { type: "UploadFixture", data: {
	/** Original filename (used to determine format and as storage name) */
	filename: string;
	/** File content as bytes */
	content: number[];
}}
	/** Delete fixtures from the library */
	| { type: "DeleteFixtures", data: FixtureLibraryEntry[] };

/** Wrapper for serializing fixture-library messages with the WebSocket wire format. */
export type FixtureLibraryWsMessage = 
	/** List of available fixtures. */
	| { type: "ListAvailableFixturesResponse", data: ListAvailableFixturesResponse }
	/** Fixture profile information. */
	| { type: "GetFixtureProfileResponse", data: GetFixtureProfileResponse }
	/** A fixture-library command retained while UI acknowledgements migrate. */
	| { type: "FixtureLibraryCommand", data: FixtureLibraryCommand };

/** Persisted fixture target affected by an intensity master. */
export type FixtureMasterTarget = 
	/** All eligible fixture intensity parameters. */
	| { type: "All", data?: undefined }
	/** Fixtures resolved from the referenced group at runtime. */
	| { type: "Group", data: Uuid }
	/** Fixtures captured by a stored spatial selection. */
	| { type: "Selection", data: SpatialSelection };

/** Wrapper for serializing fx messages with WsOutbound-compatible format */
export type FixtureWsMessage = 
	/** List of all fixtures (without geometry) */
	| { type: "FixtureDefinitions", data: Fixture[] }
	/** Geometry data for fixtures, keyed by fixture UID */
	| { type: "FixtureGeometries", data: Record<string, FixtureGeometry> }
	/** DMX universes data */
	| { type: "DmxUniverseData", data: OutboundDmxUniverse[] }
	/** Input contribution trace data. */
	| { type: "InputContributionTrace", data: OutboundInputContribution[] }
	/** Binding definitions for fixtures and transports. */
	| { type: "Bindings", data: BindingsSnapshot }
	/** Fixture and fixture element color path defaults. */
	| { type: "ColorPathDefaults", data: ColorPathDefault[] }
	/** Current binding overlap validation settings. */
	| { type: "BindingValidationSettings", data: BindingValidationSettings }
	/** Fixture command (for forwarding CRUD operations to UI) */
	| { type: "FixtureCommand", data: FixtureCommand }
	/** Current absolute, relative, and output parameter values. */
	| { type: "ParameterState", data: OutboundParameterState[] };

/** Engine commands for flow operations. */
export type FlowCommand = 
	/** Store a flow definition. */
	| { type: "StoreFlow", data: FlowDefinition }
	/** Rename a flow definition. */
	| { type: "RenameFlow", data: {
	/** ID of the flow to rename. */
	id: number;
	/** New ID for the flow. */
	new_id: number;
}}
	/** Delete a flow definition. */
	| { type: "DeleteFlow", data: number }
	/** Apply a flow delta to a definition. */
	| { type: "ApplyDelta", data: FlowDelta }
	/** Start a flow playback. */
	| { type: "StartFlow", data: number }
	/** Stop a flow playback. */
	| { type: "StopFlow", data: number }
	/** Go/advance a flow playback. */
	| { type: "GoFlow", data: number };

/** Wrapper for serializing flow websocket messages. */
export type FlowWsMessage = 
	/** List of all flow definitions. */
	| { type: "FlowDefinitions", data: FlowDefinition[] }
	/** List of all registered node templates. */
	| { type: "FlowNodeTemplates", data: FlowNodeDescriptor[] }
	/** A flow command for reactive UI handling. */
	| { type: "FlowCommand", data: FlowCommand }
	/** Applied delta to a flow definition. */
	| { type: "FlowDelta", data: FlowDelta }
	/** Flow delta acknowledgement. */
	| { type: "FlowAck", data: FlowAck }
	/** Flow snapshot (rare; full runtime state). */
	| { type: "FlowSnapshot", data: FlowSnapshot }
	/** Port value delta. */
	| { type: "FlowPortValueDelta", data: FlowPortValueDelta }
	/** Trigger delta. */
	| { type: "FlowTriggerDelta", data: FlowTriggerDelta };

/** Commands for cue-related operations */
export type FxCommand = 
	/** Store a fx */
	| { type: "StoreFx", data: Fx }
	/** Rename a fx */
	| { type: "RenameFx", data: {
	/** ID of the fx */
	id: number;
	/** New ID for the fx */
	new_id: number;
}}
	/** Delete a fx */
	| { type: "DeleteFx", data: number };

/** Commands for stored fx module management and control. */
export type FxModuleCommand = 
	/** Create or update a stored fx module object. */
	| { type: "StoreFxModule", data: StoredFxModuleRequest }
	/** Move a stored fx module object to a different numeric ID. */
	| { type: "MoveFxModule", data: {
	/** Current numeric ID of the stored fx module object. */
	id: number;
	/** Destination numeric ID for the stored fx module object. */
	new_id: number;
}}
	/** Remove a stored fx module object by numeric ID. */
	| { type: "DeleteFxModule", data: number }
	/** Restore an exactly identified stored fx module removed by undoable deletion. */
	| { type: "RestoreDeletedFxModule", data: StoredFxModule }
	/** Start or stop a stored fx module object. */
	| { type: "ControlFxModule", data: FxModuleControlAction }
	/** List WebAssembly fx modules currently available on disk. */
	| { type: "ListAvailableFxModules", data?: undefined };

/** Lifecycle control commands for stored fx module objects. */
export type FxModuleControlAction = 
	/** Start the addressed stored fx module object. */
	| { type: "Start", data: number }
	/** Stop the addressed stored fx module object. */
	| { type: "Stop", data: number };

/** High-frequency FX module preview changes during editing. */
export type FxModulePreviewUpdate = 
	/** Start previewing a fx module definition. */
	| { type: "StartPreview", data: StoredFxModule }
	/** Update the active fx module preview, recreating isolated runtime state. */
	| { type: "UpdatePreview", data: StoredFxModule }
	/** Stop preview and release previewed layers. */
	| { type: "StopPreview", data?: undefined };

/** Websocket messages exchanged with FX module clients. */
export type FxModuleWsMessage = 
	| { type: "FxModuleDefinitions", data: StoredFxModule[] }
	| { type: "ListAvailableFxModulesResponse", data: ListAvailableFxModulesResponse }
	| { type: "FxModuleCommand", data: FxModuleCommand };

/** High-frequency FX preview changes during editing. */
export type FxPreviewUpdate = 
	/** Start previewing an FX configuration (spawns singleton preview entity) */
	| { type: "StartPreview", data: Fx }
	/** Update the preview with new configuration */
	| { type: "UpdatePreview", data: Fx }
	/** Stop preview and clean up */
	| { type: "StopPreview", data?: undefined };

/** Wrapper for serializing fx messages with WsOutbound-compatible format */
export type FxWsMessage = 
	/** List of all fx */
	| { type: "FxDefinitions", data: Fx[] }
	/** List of all step-based fx */
	| { type: "StepFxDefinitions", data: StepFx[] }
	/** An fx command for reactive UI handling */
	| { type: "FxCommand", data: FxCommand };

/** Grid dimensions used when projecting a linear selection into rows and columns. */
export type GridSize = 
	| { type: "Width", data: number }
	| { type: "WidthHeight", data: {
	x: number;
	y: number;
}};

/** Events related to group management */
export type GroupCommand = 
	/** Store or update a group */
	| { type: "StoreGroup", data: Group }
	/** Rename a group */
	| { type: "RenameGroup", data: {
	/** ID of the group */
	id: number;
	/** New ID for the group */
	new_id: number;
}}
	/** Delete a group */
	| { type: "DeleteGroup", data: number };

/** Group reference expression with explicit identity semantics. */
export type GroupRefExpr = 
	/** Stable reference to a specific group object. */
	| { type: "ByUid", data: {
	uid: string;
}}
	/**
	 * Alias reference to the group currently assigned this user-facing ID.
	 * 
	 * Persistence boundaries stabilize resolvable aliases to `ByUid` by default.
	 */
	| { type: "ById", data: number }
	/**
	 * Alias reference to the group currently matching this label.
	 * 
	 * Persistence boundaries stabilize resolvable aliases to `ByUid` by default.
	 */
	| { type: "ByLabel", data: string }
	/**
	 * Inclusive alias range over user-facing group IDs.
	 * 
	 * Persistence boundaries expand resolvable members to `ByUid` references by default.
	 */
	| { type: "RangeById", data: {
	/** Start ID. */
	start: number;
	/** End ID. */
	end: number;
}}
	/**
	 * Persisted marker for an authored numeric alias that could not be resolved.
	 * 
	 * This marker never binds to a group that later acquires the same ID.
	 */
	| { type: "MissingById", data: number }
	/**
	 * Persisted marker for an authored label alias that could not be resolved.
	 * 
	 * This marker never binds to a group that later acquires the same label.
	 */
	| { type: "MissingByLabel", data: string }
	/** Addition of two group reference expressions. */
	| { type: "Add", data: {
	/** Left-hand side of the addition. */
	lhs: GroupRefExpr;
	/** Right-hand side of the addition. */
	rhs: GroupRefExpr;
}}
	/** Subtraction of two group reference expressions. */
	| { type: "Sub", data: {
	/** Left-hand side of the subtraction. */
	lhs: GroupRefExpr;
	/** Right-hand side of the subtraction. */
	rhs: GroupRefExpr;
}}
	/** Parenthesized expression that preserves span boundaries. */
	| { type: "Span", data: GroupRefExpr };

/**
 * Generic identifier expression for any object type (groups, cues, sequences, macros, etc.)
 * Represents set operations over numeric IDs - resolution happens at usage site with context.
 */
export type IdExpr = 
	/** A single ID */
	| { type: "Single", data: number }
	/** A quoted label resolved in the target object namespace. */
	| { type: "Label", data: string }
	/** A range of IDs (start thru end, inclusive) */
	| { type: "Range", data: {
	/** Start ID */
	start: number;
	/** End ID */
	end: number;
}}
	/** Addition of two expressions */
	| { type: "Add", data: {
	/** Left-hand side of the addition */
	lhs: IdExpr;
	/** Right-hand side of the addition */
	rhs: IdExpr;
}}
	/** Subtraction of two expressions */
	| { type: "Sub", data: {
	/** Left-hand side of the subtraction */
	lhs: IdExpr;
	/** Right-hand side of the subtraction */
	rhs: IdExpr;
}}
	/** Parenthesized expression (preserves span boundaries for effects) */
	| { type: "Span", data: IdExpr };

/** Commands for controlling running instances by `InstanceId`. */
export type InstanceCommand = 
	/** Advance a running instance, such as to the next cue in a sequence. */
	| { type: "Go", data: InstanceId }
	/** Jump to a specific position in an instance. */
	| { type: "Goto", data: {
	instance_id: InstanceId;
	position: number;
}}
	/** Stop a running instance. */
	| { type: "Stop", data: InstanceId }
	/** Stop all running instances. */
	| { type: "StopAll", data?: undefined }
	/** Spawn a new instance from a sequence source. */
	| { type: "StartSequence", data: {
	sequence_id: Uuid;
	priority: Priority;
}}
	/** Spawn a new instance from an FX source. */
	| { type: "StartFx", data: {
	fx_id: Uuid;
	priority: Priority;
}}
	/** Stop all instances matching a tag. */
	| { type: "StopByTag", data: string }
	/** Stop all instances of a specific kind. */
	| { type: "StopByKind", data: InstanceKind };

/** High-frequency updates applied to `InstanceControls` without a user-command lifecycle. */
export type InstanceControlUpdate = 
	/** Set the intensity scale for a playback. */
	| { type: "SetIntensityScale", data: {
	instance_id: InstanceId;
	value: number;
}}
	/** Set the rate multiplier for a playback. */
	| { type: "SetRate", data: {
	instance_id: InstanceId;
	value: number;
}};

/** Persisted instance target affected by a rate master. */
export type InstanceMasterTarget = 
	/** All live non-preview instances. */
	| { type: "All", data?: undefined }
	/** Live instances attached to these clip IDs. */
	| { type: "Clips", data: number[] };

/** Grouping rule used when inverting a projected spatial selection. */
export enum InvertMode {
	Index = "Index",
	Block = "Block",
	Group = "Group",
	Wing = "Wing",
}

/** Observable state of registry lookup, dispatch, or terminal domain work. */
export type InvocationOutcome = 
	/** The owning domain accepted work whose terminal outcome is not yet known. */
	| { type: "Accepted", data?: undefined }
	/** The owning domain completed the invocation successfully. */
	| { type: "Succeeded", data: {
	/** Optional domain-owned output erased at the registry boundary. */
	output?: unknown;
}}
	/** The registry or owning domain rejected the invocation. */
	| { type: "Failed", data: InvocationError };

/** User and UI commands that mutate master configuration or levels. */
export type MasterCommand = 
	/** Store or replace a master definition. */
	| { type: "StoreMaster", data: Master }
	/** Rename a master numeric ID. */
	| { type: "RenameMaster", data: {
	/** Existing master ID. */
	id: number;
	/** New master ID. */
	new_id: number;
}}
	/** Delete a master by numeric ID. */
	| { type: "DeleteMaster", data: number }
	/** Sets a master's control level. */
	| { type: "SetMasterLevel", data: {
	/** Master numeric ID. */
	id: number;
	/** New level in percent. */
	level_percent: number;
}}
	/** Set a master's activation mode. */
	| { type: "SetMasterMode", data: {
	/** Master numeric ID. */
	id: number;
	/** New activation mode. */
	mode: MasterMode;
}}
	/** Toggle a toggle-mode master. */
	| { type: "ToggleMaster", data: {
	/** Master numeric ID. */
	id: number;
}};

/** Commands for MIDI operations */
export type MidiCommand = 
	/** Store/replace all mappings */
	| { type: "StoreMappings", data: MidiMapping[] }
	/** Delete a mapping by index */
	| { type: "DeleteMapping", data: number };

/** WebSocket message types for MIDI */
export type MidiWsMessage = 
	/** List of connected MIDI devices */
	| { type: "MidiDeviceList", data: MidiDevice[] }
	/** Current MIDI mappings configuration */
	| { type: "MidiMappings", data: MidiMapping[] }
	/** Last received MIDI event (for identification) */
	| { type: "MidiLastEvent", data: MidiLastEvent };

/** Commands for object library operations */
export type ObjectLibraryCommand = 
	/** List all available objects in the library */
	| { type: "ListAvailableObjects", data?: undefined }
	/** Get detailed information about a specific object */
	| { type: "GetObjectProfile", data: {
	/** Object name */
	name: string;
}}
	/** Refresh the object library by rescanning the directory */
	| { type: "RefreshLibrary", data?: undefined }
	/** Create an object bundle from a GLB file */
	| { type: "CreateObject", data: {
	/** Object metadata */
	metadata: ObjectMetadata;
	/** GLB file content as bytes */
	glb_content: number[];
}}
	/** Upload an object bundle (.robj) to the library */
	| { type: "UploadObject", data: {
	/** Original filename (used as storage name) */
	filename: string;
	/** File content as bytes */
	content: number[];
}}
	/** Delete objects from the library */
	| { type: "DeleteObjects", data: string[] }
	/** Create a scene object from the library and add it to the show */
	| { type: "CreateSceneObjectFromLibrary", data: {
	/** Scene object ID */
	id: number;
	/** Object name in the library */
	object_name: string;
	/** Optional user-defined label */
	label?: string;
	/** Existing scene object IDs to update to this library asset version */
	update_existing_ids?: number[];
}}
	/** Update existing scene objects in the showfile using their linked library entries */
	| { type: "UpdateSceneObjectsFromLibrary", data: {
	/** Scene object IDs to update from library definitions */
	scene_object_ids: number[];
}};

/** Wrapper for serializing object-library messages with the WebSocket wire format. */
export type ObjectLibraryWsMessage = 
	/** List of available objects. */
	| { type: "ListAvailableObjectsResponse", data: ListAvailableObjectsResponse }
	/** Object profile information. */
	| { type: "GetObjectProfileResponse", data: GetObjectProfileResponse }
	/** An object-library command retained for wire-schema compatibility during UI migration. */
	| { type: "ObjectLibraryCommand", data: ObjectLibraryCommand };

/** Commands accepted by the OSC plugin. */
export type OscCommand = 
	/** Replace all mappings. */
	| { type: "StoreMappings", data: OscMapping[] }
	/** Delete mapping by index. */
	| { type: "DeleteMapping", data: number };

/** Websocket messages emitted by the OSC plugin. */
export type OscWsMessage = 
	/** Known source list. */
	| { type: "OscSources", data: OscSource[] }
	/** Configured mappings. */
	| { type: "OscMappings", data: OscMapping[] }
	/** Last observed OSC event. */
	| { type: "OscLastEvent", data: OscLastEvent }
	/** Listener bind status. */
	| { type: "OscListenerStatus", data: OscListenerStatus }
	/** External eval metadata for CommandLine source tagging. */
	| { type: "OscExternalEval", data: OscExternalEval };

/** Closed-world source category used for planner/materializer dispatch. */
export enum PlannedPlaybackSourceKind {
	/** Cue source category. */
	Cue = "cue",
	/** Cue sequence source category. */
	Sequence = "sequence",
	/** Classic FX source category. */
	Fx = "fx",
	/** Step FX source category. */
	StepFx = "step_fx",
	/** Wasm FX module source category. */
	FxModule = "fx_module",
	/** Flow source category. */
	Flow = "flow",
}

/** Events related to the programmer */
export type ProgrammerCommand = 
	/** Clear selection if present; otherwise clear active programmer values */
	| { type: "ClearProgrammer", data?: undefined }
	/** Clear the active selection in the programmer */
	| { type: "ClearProgrammerSelection", data?: undefined }
	/** Clear the active programmer values */
	| { type: "ClearProgrammerValues", data?: undefined }
	/**
	 * Release programmer values for an optional selection and optional attributes.
	 * If selection is None, this applies globally across programmer instructions.
	 */
	| { type: "ReleaseProgrammerValues", data: {
	/** Optional selection scope. None applies globally. */
	selection?: SelectionExpr;
	/** Optional attribute filter. Empty means all attributes. */
	attributes: Attribute[];
	/** Whether the caller explicitly approved flattening non-resolved selections. */
	allow_selection_flatten?: boolean;
	/** Single-use backend approval proving that an operator confirmed flattening. */
	selection_flatten_approval?: Uuid;
}}
	/** Add an instruction to the active programmer. */
	| { type: "AddProgrammerInstruction", data: {
	/** Spatial selection to apply instruction to. */
	selection: SpatialSelection;
	/** Instruction to add. */
	instruction: CueInstruction;
}}
	/** Add an instruction to the active programmer with the current selection */
	| { type: "AddProgrammerInstructionWithActiveSelection", data: CueInstruction }
	/** Resolve and apply ordered direct or Blueprint-backed attribute operations. */
	| { type: "ApplyAttributeOperations", data: {
	/** Explicit selection, or `None` to bind the active selection at execution. */
	selection?: SpatialSelection;
	/** Ordered operations produced by the shared attribute-assignment parser. */
	operations: ProgrammerAttributeOperation[];
	/** Instruction-wide timing applied to each generated ordered row. */
	transitions: PartialTransition;
	/** Per-attribute timing retained on generated rows. */
	transitions_by_attribute: AttributeTransitions;
}}
	/** Set the active selection in the programmer */
	| { type: "SetProgrammerSelection", data: SelectionExpr }
	/** Set the active spatial selection in the programmer */
	| { type: "SetProgrammerSpatialSelection", data: SpatialSelection }
	/** Add to the active selection in the programmer */
	| { type: "AddProgrammerSelection", data: SelectionExpr }
	/** Remove from the active selection in the programmer */
	| { type: "RemoveProgrammerSelection", data: SelectionExpr }
	/** Save programmer instructions as a cue */
	| { type: "StoreCue", data: {
	/** Sequence ID to store the cue into */
	sequence_id: number;
	/** Cue ID target within the sequence */
	cue_id: StoreCueId;
	/** Cue part target within the cue. Part 0 is the parent cue. */
	part_id: StoreCuePartId;
	/** Store behavior for existing targets */
	mode: StoreMode;
	/** Optional label for the cue or part */
	label?: string;
}}
	/** Recall a cue into the active programmer */
	| { type: "RecallCue", data: {
	/** Sequence ID containing the cue */
	sequence_id: number;
	/** Cue ID to recall */
	cue_id: number;
	/** Cue part ID to recall. Part 0 is the parent cue. */
	part_id: number;
	/** Whether recall should select the recalled fixtures or elements. */
	select: boolean;
}}
	/** Store current programmer selection as a group */
	| { type: "StoreGroup", data: {
	/** Group ID to store into */
	group_id: number;
	/** Optional label for the group */
	label?: string;
}}
	/** Store current programmer values as a blueprint */
	| { type: "StoreBlueprint", data: {
	/** Blueprint ID to store into */
	blueprint_id: number;
	/** Optional attribute/category filter (empty = all attributes) */
	filter: BlueprintSelector[];
}};

/** Wrapper for serializing programmer messages with WsOutbound-compatible format */
export type ProgrammerWsMessage = 
	/** Adapts the internal state to a fixture UID-keyed map of parameter values */
	| { type: "ProgrammerState", data: OutboundProgrammerElementState[] }
	/**
	 * Current programmer selection (fixture UIDs). Has a dedicated
	 * command instead of forwarding ProgrammerCommand::SetProgrammerSelection
	 * because the UI cannot resolve a SelectionExpr for now.
	 */
	| { type: "ProgrammerSelection", data: string[] }
	/** Current programmer spatial selection and its resolved index view. */
	| { type: "ProgrammerSpatialSelection", data: OutboundProgrammerSpatialSelection }
	/** Programmer commands for reactive UI handling. */
	| { type: "ProgrammerCommand", data?: undefined };

/** sACN delivery mode for a universe. */
export type SacnDelivery = 
	/** Use the protocol multicast destination for the universe. */
	| { type: "Multicast", data?: undefined }
	/** Unicast to a specific IP address. */
	| { type: "Unicast", data: {
	/** Target IP address. */
	ip: Ipv4Addr;
}};

/** Commands for scene object operations */
export type SceneObjectCommand = 
	/** Store a scene object (create or update) */
	| { type: "StoreSceneObject", data: SceneObject }
	/** Delete a scene object by ID */
	| { type: "DeleteSceneObject", data: number }
	/** Update scene object 3D placement (position and/or rotation) */
	| { type: "UpdateSceneObjectPlacement", data: {
	/** ID of the scene object */
	id: number;
	/** Position update (None = no change) */
	position?: SceneObjectPlacementPositionUpdate;
	/** Rotation update (None = no change) */
	rotation?: SceneObjectPlacementRotationUpdate;
}}
	/** Update scene object properties */
	| { type: "UpdateSceneObjectProperties", data: {
	/** ID of the scene object */
	id: number;
	/** New properties for the scene object */
	properties: SceneObjectProperties;
}};

/** Update to scene object 3D position (all coordinates or individual axis) */
export type SceneObjectPlacementPositionUpdate = 
	/** Set all position coordinates at once */
	| { type: "All", data: SceneObjectPosition }
	/** Set only X coordinate */
	| { type: "X", data: number }
	/** Set only Y coordinate */
	| { type: "Y", data: number }
	/** Set only Z coordinate */
	| { type: "Z", data: number };

/** Update to scene object 3D rotation (all angles or individual axis) */
export type SceneObjectPlacementRotationUpdate = 
	/** Set all rotation angles at once */
	| { type: "All", data: SceneObjectRotation }
	/** Set only X rotation (pitch) */
	| { type: "X", data: number }
	/** Set only Y rotation (yaw) */
	| { type: "Y", data: number }
	/** Set only Z rotation (roll) */
	| { type: "Z", data: number };

/** Wrapper for serializing scene object messages with WsOutbound-compatible format */
export type SceneObjectWsMessage = 
	/** List of all scene objects */
	| { type: "SceneObjectDefinitions", data: SceneObject[] }
	/** Scene object command (for forwarding CRUD operations to UI) */
	| { type: "SceneObjectCommand", data: SceneObjectCommand };

/** Selection type for programmer */
export enum SelectionType {
	Fixture = "Fixture",
	Group = "Group",
}

/**
 * Engine actions for sequence playback operations.
 * 
 * These actions operate on running sequence instances (`MaterializedSequence`).
 * They are typically delegated from ClipCommand to enable proper undo
 * with shared undo identity; the sequence owns its position state and undo logic.
 */
export type SequencePlaybackAction = 
	/** Advance the sequence to the next cue. */
	| { type: "Go", data: {
	/** The playback to advance */
	instance_id: InstanceId;
}}
	/** Move the sequence to the previous cue. */
	| { type: "Back", data: {
	/** The playback to move backward */
	instance_id: InstanceId;
}}
	/** Jump to a specific position in the sequence. */
	| { type: "Goto", data: {
	/** The playback to modify */
	instance_id: InstanceId;
	/** The position (1-based cue number) to jump to */
	position: number;
	/** Optional reconstruction timing to apply to the entered cue's transition. */
	timing?: PlaybackReconstructionTiming;
}}
	/** Render the sequence at a planner-derived source-local position. */
	| { type: "RenderAt", data: {
	/** The playback to render */
	instance_id: InstanceId;
	/** The position (1-based cue number) to render */
	position: number;
	/** Reconstruction timing to apply to the active cue and playback clock. */
	timing: PlaybackReconstructionTiming;
}}
	/** Stop and release the sequence playback. */
	| { type: "Stop", data: {
	/** The playback to stop */
	instance_id: InstanceId;
}};

/** Commands for modifying desk-wide settings */
export type SettingsCommand = 
	/** Set the programmer auto-select behavior */
	| { type: "SetProgrammerAutoSelect", data: boolean }
	/** Set the selected network interface (None = system default) */
	| { type: "SetNetworkInterface", data?: string }
	/** Sets host-owned external control permission and interface selection. */
	| { type: "SetExternalControl", data: ExternalControlSettings }
	/** Set whether network DMX output is enabled */
	| { type: "SetNetworkOutputEnabled", data: boolean }
	/** Set whether network DMX input is enabled */
	| { type: "SetNetworkInputEnabled", data: boolean }
	/** Set whether USB DMX output is enabled */
	| { type: "SetUsbOutputEnabled", data: boolean }
	/** Set named network DMX output targets. */
	| { type: "SetNetworkDmxOutputs", data: NetworkDmxOutputTargets }
	/** Set named USB DMX output targets. */
	| { type: "SetUsbDmxOutputs", data: UsbDmxOutputTargets }
	/** Set the input signal-loss policy */
	| { type: "SetInputSignalLossPolicy", data: InputSignalLossPolicy }
	/** Set the stale input age threshold */
	| { type: "SetInputSignalLossTimeout", data: Duration }
	/** Set patch overlap validation mode */
	| { type: "SetBindingValidationMode", data: BindingValidationMode }
	/** Set the selected audio device (None = system default) */
	| { type: "SetAudioDevice", data?: string }
	/** Set cue renumbering behavior when sequence steps are reordered in UI */
	| { type: "SetSequenceReorderRenumberPolicy", data: SequenceReorderRenumberPolicy }
	/** Set whether input views show only external inputs or all detected inputs */
	| { type: "SetInputUniverseVisibilityMode", data: InputUniverseVisibilityMode }
	/** Set behavior for operations that may flatten programmer selection expressions */
	| { type: "SetSelectionFlattenPolicy", data: SelectionFlattenPolicy }
	/** Set preferred display unit for editable time values */
	| { type: "SetTimeDisplayPreference", data: TimeDisplayPreference }
	/** Set whether timeline insert and paste operations use the playhead or cursor */
	| { type: "SetTimelinePlacementPreference", data: TimelinePlacementPreference }
	/** Set number of timestamped showfile backups to retain; 0 means unlimited */
	| { type: "SetShowfileBackupRetention", data: number }
	/** Set showfile-scoped named UI panel layouts */
	| { type: "SetPanelLayouts", data: StoredPanelLayout[] }
	/** Set showfile-scoped active UI panel layout */
	| { type: "SetActivePanelLayout", data?: ActivePanelLayout }
	/** Request list of available network interfaces */
	| { type: "GetAvailableNetworkInterfaces", data?: undefined }
	/** Request list of available audio devices */
	| { type: "GetAvailableAudioDevices", data?: undefined }
	/** Request list of compatible USB DMX devices */
	| { type: "GetAvailableUsbDmxDevices", data?: undefined };

/** Commands for step-based FX operations. */
export type StepFxCommand = 
	/**
	 * Create a Step FX from command-authored values whose Blueprint addresses
	 * still need to be resolved against current engine state.
	 */
	| { type: "Create", data: StepFxDraft }
	/** Create or replace one complete valid Step FX definition. */
	| { type: "Store", data: StepFx }
	/** Delete a stored Step FX by numeric ID. */
	| { type: "Delete", data: number }
	/** Start a step FX */
	| { type: "Start", data: number }
	/** Stop a step FX */
	| { type: "Stop", data: number }
	/** Set rate multiplier for a step FX */
	| { type: "SetRate", data: {
	/** ID of the FX */
	fx_id: number;
	/** Rate multiplier */
	rate: number;
}};

/** High-frequency complete-draft updates for Step FX editor previews. */
export type StepFxPreviewUpdate = 
	/** Starts a session-specific preview from a complete valid draft. */
	| { type: "Start", data: {
	/** Editor-owned preview session identity. */
	session_id: StepFxPreviewSessionId;
	/** Complete valid draft to evaluate. */
	step_fx: StepFx;
}}
	/** Replaces a running session's draft while preserving normalized cycle phase. */
	| { type: "Update", data: {
	/** Editor-owned preview session identity. */
	session_id: StepFxPreviewSessionId;
	/** Complete valid draft to evaluate. */
	step_fx: StepFx;
}}
	/** Stops only the addressed editor preview session. */
	| { type: "Stop", data: {
	/** Editor-owned preview session identity. */
	session_id: StepFxPreviewSessionId;
}};

/** Cue ID target for a cue store command. */
export type StoreCueId = 
	/** Store into the exact cue ID. */
	| { type: "Exact", data: number }
	/** Store into the next available cue ID in the sequence. */
	| { type: "Next", data?: undefined };

/** Part ID target for a cue-part store command. */
export type StoreCuePartId = 
	/** Store into the exact part ID. */
	| { type: "Exact", data: number }
	/** Store into the next available part ID in the cue. */
	| { type: "Next", data?: undefined };

/** Store behavior requested by a cue store command. */
export enum StoreMode {
	/** Replace target contents with programmer values. */
	Replace = "replace",
	/** Upsert programmer values into target contents. */
	Merge = "merge",
	/** Update only existing fixture and attribute values in target contents. */
	Update = "update",
	/** Remove programmer attributes from target contents. */
	Remove = "remove",
}

/** Engine commands for timecode actions */
export type TimecodeCommand = 
	/** Start a timecode */
	| { type: "StartTimecode", data: number }
	/** Pause a timecode */
	| { type: "PauseTimecode", data: number }
	/** Stop a timecode */
	| { type: "StopTimecode", data: number }
	/** Seek a timecode */
	| { type: "SeekTimecode", data: {
	/** ID of the timecode to seek */
	id: number;
	/** Position to seek to */
	position: Duration;
}}
	/** Store a timecode */
	| { type: "StoreTimecode", data: Timecode }
	/** Rename a timecode */
	| { type: "RenameTimecode", data: {
	/** ID of the timecode to rename */
	id: number;
	/** New ID for the timecode */
	new_id: number;
}}
	/** Delete a timecode */
	| { type: "DeleteTimecode", data: number };

/** Wrapper for serializing timeline messages with WsOutbound-compatible format */
export type TimecodeWsMessage = 
	/** List of all timecodes */
	| { type: "TimecodeDefinitions", data: OutboundTimecodeLocal[] };

/** One audio side effect requested by the authoritative timeline domain. */
export type TimelineAudioDirective = 
	/** Load one logical bundled asset for a timeline. */
	| { type: "Load", data: {
	/** Timeline definition requesting playback. */
	timeline_uid: string;
	/** Logical asset identifier resolved by the browser host. */
	asset_id: string;
}}
	/** Begin or resume playback from the supplied authoritative offset. */
	| { type: "Play", data: {
	/** Timeline definition requesting playback. */
	timeline_uid: string;
	/** Requested media offset. */
	offset: Duration;
}}
	/** Pause playback while retaining the loaded media and position. */
	| { type: "Pause", data: {
	/** Timeline definition requesting the pause. */
	timeline_uid: string;
}}
	/** Seek loaded media to the supplied authoritative offset. */
	| { type: "Seek", data: {
	/** Timeline definition requesting the seek. */
	timeline_uid: string;
	/** Requested media offset. */
	offset: Duration;
}}
	/** Stop playback and return media to the beginning. */
	| { type: "Stop", data: {
	/** Timeline definition requesting the stop. */
	timeline_uid: string;
}}
	/** Apply the persisted timeline loop range to browser media. */
	| { type: "SetLoop", data: {
	/** Timeline definition owning the loop. */
	timeline_uid: string;
	/** Enabled loop range, or `None` when looping is disabled. */
	range?: TimelineAudioLoopRange;
}}
	/** Release loaded media for an inactive timeline. */
	| { type: "Unload", data: {
	/** Timeline definition whose media is no longer needed. */
	timeline_uid: string;
}};

/** User-facing commands for timeline runtime and persistence changes. */
export type TimelineCommand = 
	/** Activate a timeline */
	| { type: "StartTimeline", data: number }
	/** Deactivate a timeline */
	| { type: "StopTimeline", data: number }
	/** Store a timeline object */
	| { type: "StoreTimeline", data: Timeline }
	/** Create a timeline and either reuse or create its same-numbered timecode. */
	| { type: "CreateTimeline", data: {
	/** Timeline definition to create. */
	timeline: Timeline;
	/** Default timecode to create when the same-numbered timecode does not exist. */
	timecode: Timecode;
}}
	/** Restore a timeline creation during redo with its original ownership semantics. */
	| { type: "RestoreTimelineCreation", data: {
	/** Timeline definition to restore. */
	timeline: Timeline;
	/** Owned timecode to restore, or `None` when the timeline used a shared timecode. */
	owned_timecode?: Timecode;
}}
	/** Delete a timeline created as one atomic operation. */
	| { type: "DeleteCreatedTimeline", data: {
	/** Exact UID of the timeline created by the operation. */
	timeline_uid: string;
	/** Exact UID of its owned timecode, if the operation created one. */
	owned_timecode_uid?: Uuid;
}}
	/** Store or update a timeline marker. */
	| { type: "StoreTimelineMarker", data: {
	/** ID of the timeline to update. */
	timeline_id: number;
	/** Marker data to store. */
	marker: TimelineMarker;
}}
	/** Delete a timeline marker. */
	| { type: "DeleteTimelineMarker", data: {
	/** ID of the timeline to update. */
	timeline_id: number;
	/** UID of the marker to delete. */
	marker_uid: string;
}}
	/** Store or update a timeline region. */
	| { type: "StoreTimelineRegion", data: {
	/** ID of the timeline to update. */
	timeline_id: number;
	/** Region data to store. */
	region: TimelineRegion;
}}
	/** Delete a timeline region. */
	| { type: "DeleteTimelineRegion", data: {
	/** ID of the timeline to update. */
	timeline_id: number;
	/** UID of the region to delete. */
	region_uid: string;
}}
	/** Set or clear the persisted loop range for a timeline. */
	| { type: "SetTimelineLoopRange", data: {
	/** ID of the timeline to update. */
	timeline_id: number;
	/** Loop range to store, or `None` to clear it. */
	loop_range?: TimelineLoopRange;
}}
	/** Set runtime-only timeline recording state. */
	| { type: "SetTimelineRecording", data: {
	/** ID of the timeline to arm or disarm. */
	timeline_id: number;
	/** Whether the timeline should record external desk actions. */
	enabled: boolean;
	/** Track ID that should receive recorded actions. */
	target_track_id?: string;
}}
	/** Insert actions captured by timeline recording. */
	| { type: "InsertRecordedActions", data: {
	/** ID of the timeline to update. */
	timeline_id: number;
	/** Track ID to insert into. */
	track_id: string;
	/** Actions captured by the recorder. */
	actions: Action[];
}}
	/** Delete recorded timeline actions by ID. */
	| { type: "DeleteRecordedActions", data: {
	/** ID of the timeline to update. */
	timeline_id: number;
	/** Track ID to delete from. */
	track_id: string;
	/** Action IDs to delete. */
	action_ids: string[];
}}
	/** Nudge selected timeline objects by a signed millisecond delta. */
	| { type: "NudgeTimelineSelection", data: {
	/** ID of the timeline to update. */
	timeline_id: number;
	/** Selected timeline objects to nudge. */
	selection: TimelineSelection[];
	/** Signed delta in milliseconds. */
	delta_ms: number;
}}
	/** Request beatgrid detection for a timeline. */
	| { type: "RequestBeatgridDetection", data: {
	/** ID of the timeline to analyze. */
	timeline_id: number;
}}
	/** Apply a previously generated beatgrid proposal. */
	| { type: "ApplyBeatgridProposal", data: {
	/** ID of the timeline to update. */
	timeline_id: number;
	/** Proposal request identifier. */
	request_id: string;
	/** Optional override for beats per bar. */
	beats_per_bar?: number;
	/** Optional beat offset (mod beats_per_bar) to choose downbeat phase. */
	downbeat_offset?: number;
}}
	/** Reject and discard a previously generated beatgrid proposal. */
	| { type: "RejectBeatgridProposal", data: {
	/** ID of the timeline whose proposal should be discarded. */
	timeline_id: number;
	/** Proposal request identifier. */
	request_id: string;
}}
	/** Set the absolute first-marker time for the persisted beatgrid and shift all markers accordingly. */
	| { type: "SetBeatgridStart", data: {
	/** ID of the timeline to update. */
	timeline_id: number;
	/** Audio fingerprint guard to avoid applying stale alignment to different audio content. */
	audio_fingerprint: string;
	/** Absolute time for beat marker index 0. */
	first_marker_time: Duration;
}}
	/** Rename a timeline object */
	| { type: "RenameTimeline", data: {
	/** ID of the timeline to rename */
	id: number;
	/** New ID for the timeline */
	new_id: number;
}}
	/** Delete a timeline object */
	| { type: "DeleteTimeline", data: number };

/** A selected timeline object that can be edited by shared timeline commands. */
export type TimelineSelection = 
	/** A timeline action. */
	| { type: "Action", data: {
	/** Track identifier containing the action. */
	track_id: string;
	/** Action identifier. */
	action_id: string;
}}
	/** A marker point. */
	| { type: "Marker", data: {
	/** Marker UID. */
	marker_uid: string;
}}
	/** A region body, preserving duration while moving. */
	| { type: "RegionBody", data: {
	/** Region UID. */
	region_uid: string;
}}
	/** A region start handle. */
	| { type: "RegionStart", data: {
	/** Region UID. */
	region_uid: string;
}}
	/** A region end handle. */
	| { type: "RegionEnd", data: {
	/** Region UID. */
	region_uid: string;
}};

/** Wrapper for serializing timeline messages with WsOutbound-compatible format */
export type TimelineWsMessage = 
	/** List of all timelines */
	| { type: "TimelineDefinitions", data: Timeline[] }
	/** Runtime recording state for timelines */
	| { type: "TimelineRecordingStates", data: TimelineRecordingState[] }
	/** Actions staged by active timeline recording sessions */
	| { type: "TimelineRecordingPreviews", data: TimelineRecordingPreview[] }
	/** Runtime lookahead eligibility state for timeline actions */
	| { type: "TimelineLookaheadActionStatuses", data: TimelineLookaheadActionStatus[] }
	/** A timeline command for reactive UI handling */
	| { type: "TimelineCommand", data: TimelineCommand }
	/** Beatgrid detection started notification */
	| { type: "BeatgridDetectionStarted", data: BeatgridDetectionStarted }
	/** Beatgrid detection proposal notification */
	| { type: "BeatgridDetectionReady", data: BeatgridDetectionReady }
	/** Beatgrid detection failure notification */
	| { type: "BeatgridDetectionFailed", data: BeatgridDetectionFailed };

/** Severity levels used when displaying UI toast messages. */
export enum ToastLevel {
	Info = "Info",
	Success = "Success",
	Warning = "Warning",
	Error = "Error",
}

/** Outbound observations requesting UI-only presentation behavior. */
export type UiNotification = 
	/** Show a toast/notification in the UI */
	| { type: "ShowToast", data: {
	level: ToastLevel;
	message: string;
}}
	/** Update the frontend's remembered current showfile name. */
	| { type: "CurrentShowfileChanged", data: {
	name?: string;
	change_id: Uuid;
}};

/**
 * Commands for undo/redo operations
 * Undo stack commands that can be applied or reversed.
 */
export type UndoCommand = 
	/** Execute undo */
	| { type: "Undo", data: {
}}
	/** Execute redo */
	| { type: "Redo", data: {
}}
	/** Clear undo history */
	| { type: "ClearHistory", data: {
}};

/** Source of a value for a cue instruction */
export type ValueSource = 
	/** An inline value */
	| { type: "Inline", data: ParameterValue }
	/**
	 * Fanned values to be interpolated across the selection.
	 * - 2 values = linear fan from start to end
	 * - 3+ values = envelope with interpolated waypoints
	 */
	| { type: "Fanned", data: {
	values: ParameterValue[];
}}
	/** Clear the tracked value for this attribute from this cue forward. */
	| { type: "Release", data?: undefined }
	/** Keep the tracked position value active and opt it out of lookahead. */
	| { type: "HoldPosition", data?: undefined };

